import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useTheme } from '../../contexts/ThemeContext';
import ScrollToBottomButton from '../chat/ScrollToBottomButton';
import { Send, Square, Upload, FileText, BarChart3, Settings, Sparkles, MessageSquare, ArrowLeft,
         Mic, Image, Paperclip, RotateCcw, Database, ArrowDown, Copy, Check, PanelLeft, Pencil, Save, Eye, Users, Scissors, Search, Lock, BookMarked, RefreshCw, Maximize2, Minimize2,
         GitCommit, BookOpen, Home, Mail, Newspaper } from 'lucide-react';
import ConsolidateModal from '../consolidation/ConsolidateModal';
import MemoryScopeToggle from '../consolidation/MemoryScopeToggle';
import SplitChatView from '../split/SplitChatView';
import usePreviewPane from '../../hooks/usePreviewPane';
import FilesInterface from '../FilesInterface';
import FilesSidebar from '../files/FilesSidebar';
import InputToolbar from '../toolbar/InputToolbar';
import TemplateManager from '../toolbar/TemplateManager';
import ConversationPills from './ConversationPills';
import ConnectedSiteBadge from '../connectors/ConnectedSiteBadge';
import ConversationDrawer from './ConversationDrawer';
import TransMemoryTab from './TransMemoryTab';
import VoiceButton from '../voice/VoiceButton';
import VoiceWidget from '../voice/VoiceWidget';
import ProjectAnalytics from './ProjectAnalytics';
import ProjectTeam from './ProjectTeam';
import ProjectSettings from './ProjectSettings';
import ExpertiseBasesPanel from '../expertise/ExpertiseBasesPanel';
import ChapterWorkspace from '../com/ChapterWorkspace';
import FamilyGraph from '../family/FamilyGraph';
import PilotMutationPanel from '../pilot/PilotMutationPanel';
import MediaCockpit from '../media/MediaCockpit';
import { getHousehold } from '../../services/familyApi';
import { appendToChapter, getBook } from '../../services/bookApi';
import { getTerms as getDocTerms } from '../../utils/documentTerminology';
import MarkdownMessage from '../chat/MarkdownMessage';
import StreamStatus from '../chat/StreamStatus';
import ActionButtons from '../chat/ActionButtons';
import CostIndicator from '../chat/CostIndicator';
import PastedContentChip from '../chat/PastedContentChip';
import ChatSearch from '../chat/ChatSearch';
import ConversationLengthIndicator from '../chat/ConversationLengthIndicator';
import PasteAttachments from '../chat/PasteAttachments';
import PlusMenu from '../chat/PlusMenu';
import FileOrganizerModal from '../files/FileOrganizerModal';
import PasteAsFileWidget from '../files/PasteAsFileWidget';
import SendToVaultModal from '../vault/SendToVaultModal';
import MailingComposerModal from '../mailing/MailingComposerModal';
import JSZip from 'jszip';
import { useStreamChat } from '../../hooks/useStreamChat';
import { useAdaptiveTypewriter } from '../../hooks/useAdaptiveTypewriter';
import { usePasteHandler } from '../../hooks/usePasteHandler';
import { toast, confirm as confirmModal } from '../ui/Toast';

// Session J+1 — Extrait un sujet d'email à partir d'un corps Markdown.
// Préfère un titre H1/H2/H3 ; sinon prend la première ligne non-vide.
// Strip syntaxe markdown courante, tronque à 100 chars, ajoute « … » si coupé.
function extractSubjectFromMarkdown(md) {
  if (!md || typeof md !== 'string') return '';
  const lines = md.split('\n').map(l => l.trim()).filter(Boolean);
  const heading = lines.find(l => /^#{1,3}\s+/.test(l));
  let raw = heading
    ? heading.replace(/^#{1,3}\s+/, '')
    : (lines[0] || '');
  // Strip emphasis, code, liens [t](u) → t, blockquote, listes
  raw = raw
    .replace(/^>\s+/, '')
    .replace(/^[-*]\s+/, '')
    .replace(/^\d+\.\s+/, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/\*([^*]+)\*/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .trim();
  if (raw.length > 100) raw = raw.slice(0, 97).trimEnd() + '…';
  return raw;
}

  const ProjectDetail = ({ projectId, onBack, user, cmActive, onFilesChanged, fileAnalyzeRequest, onProjectUpdated, onSplitViewChange, onBookWorkspaceChange, isFullScreen, initialSessionId, onInitialSessionConsumed, focusMode, onFocusToggle, projects = [] }) => {
  const { isDark } = useTheme();
  // S57-G — persiste l'onglet actif par projet pour ne pas resortir de l'Atelier au refresh
  const [activeTab, setActiveTabState] = useState(() => {
    try {
      const stored = localStorage.getItem(`itriia_project_tab_v1_${projectId}`);
      return stored || 'chat';
    } catch { return 'chat'; }
  });
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    try { localStorage.setItem(`itriia_project_tab_v1_${projectId}`, tab); } catch {}
    // Broadcast pour que FilesSidebar (rendue dans App.js, en dehors de ProjectDetail)
    // puisse adapter son affichage (filtre Famille, etc.)
    window.dispatchEvent(new CustomEvent('itriia-project-tab', { detail: { projectId, tab } }));
  };
  // Broadcast aussi au mount (ex : au chargement initial on est sur 'chat' ou restauré depuis localStorage)
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('itriia-project-tab', { detail: { projectId, tab: activeTab } }));
  }, [projectId, activeTab]);
  const { previewContent: projPreviewContent, isPreviewOpen: projPreviewOpen, showPreview: projShowPreviewRaw, closePreview: projClosePreview } = usePreviewPane();
  const projShowPreview = (content) => { projShowPreviewRaw(content); };
  const [projectData, setProjectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showAllHistory, setShowAllHistory] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  // Ref miroir : loadSessions() est une closure créée à chaque render, elle capture
  // la valeur de activeSessionId du render courant. Quand handleNewSession pose
  // setActiveSessionId(newId) puis appelle immédiatement loadSessions(), la closure
  // qui s'exécute lit encore l'ancienne valeur (null) → !activeSessionId=true →
  // auto-select kicks → wipe placeholder streaming. Le ref suit le state en temps
  // réel et coupe la stale closure à la racine, pour tous les chemins (handleNew,
  // clic card FD, etc.).
  const activeSessionIdRef = useRef(null);
  const [editingTitle, setEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [forcedModel, setForcedModel] = useState(null);
  // Ref synchrone miroir de forcedModel : l'auto-send (cards profil avec model forcé)
  // déclenche sendMessage avant que le setState forcedModel soit committé au render
  // → le state lu serait null et le modèle de la card (ex. Sonnet 4.6) ignoré (Haiku
  // par défaut). La ref garantit la valeur immédiate côté envoi.
  const forcedModelRef = useRef(null);
  const [showVoiceWidget, setShowVoiceWidget] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const lastUserMsgRef = useRef(null);
  const shouldScrollToUserMsg = useRef(false);
  const streamingMsgRef = useRef(null);
  // Session Greg — addendum système one-shot (injecté dans le 1er sendStreamMessage après clic card)
  const pendingSystemAddendumRef = useRef(null);
  const pendingAutoSendRef = useRef(false);
  // Session Fondateurs — race guard : quand un profile_initial_prompt pilote le flow
  // (création de session + auto-send), loadSessions + handleSelectSession ne doivent
  // PAS écraser l'activeSessionId ni le chatMessages en parallèle.
  // Sans ce ref, le loadSessions résout APRÈS le handleNewSession du flow profile,
  // voit !activeSessionId (stale), et force handleSelectSession(oldSessionId)
  // qui écrase le AI placeholder → le streaming arrive mais n'a plus de cible.
  // Symptôme : chat "tourne dans le vide", refresh nécessaire pour voir la réponse
  // (qui est bien persistée en DB).
  const profileFlowPendingRef = useRef(false);
  // Garde activeSessionIdRef en sync avec le state à chaque render.
  useEffect(() => { activeSessionIdRef.current = activeSessionId; }, [activeSessionId]);
  const textareaRef = useRef(null);
  // P3.4 — Ouverture directe Atelier si book.open_at_entry === true ET pas de choix user mémorisé
  const initialAtelierDecidedRef = useRef(false);
  useEffect(() => {
    if (initialAtelierDecidedRef.current) return;
    const bookId = projectData?.book_id;
    if (!bookId) return;
    const stored = localStorage.getItem(`itriia_project_tab_v1_${projectId}`);
    if (stored) { initialAtelierDecidedRef.current = true; return; }
    initialAtelierDecidedRef.current = true;
    getBook(bookId).then(res => {
      if (res?.book?.open_at_entry) setActiveTab('book');
    }).catch(() => {});
  }, [projectData?.book_id, projectId]);
  // S57 — showScrollBtn state retiré, géré par <ScrollToBottomButton> en interne
  const [copiedMsgId, setCopiedMsgId] = useState(null);
  const [savingMsgId, setSavingMsgId] = useState(null);
  const [savedMsgIds, setSavedMsgIds] = useState(new Set());
  const [appendingMsgId, setAppendingMsgId] = useState(null);
  // Session F — Coffre-fort
  const [vaultModal, setVaultModal] = useState(null); // { messageId, conversationId, role, content } | null
  // Session J+1 — Composer mailing pré-rempli depuis un message IA
  const [mailingModal, setMailingModal] = useState(null); // { subject, bodyMd } | null
  const [vaultedMsgIds, setVaultedMsgIds] = useState(new Set());
  const [appendedMsgIds, setAppendedMsgIds] = useState(new Set());
  const [savePromptMsgId, setSavePromptMsgId] = useState(null);
  const [saveFileName, setSaveFileName] = useState('');
  const [sessionCost, setSessionCost] = useState(0);
  const [projectBudget, setProjectBudget] = useState(null);
  const [expertiseBasesCount, setExpertiseBasesCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [allSessions, setAllSessions] = useState([]); // inclut archivées (pour le drawer)
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  // S59 — FileOrganizer
  const [showOrganizerModal, setShowOrganizerModal] = useState(false);
  const [organizerCache, setOrganizerCache] = useState({});
  const getOrganizerCache = React.useCallback((pid, key) => organizerCache[`${pid}:${key}`] || null, [organizerCache]);
  const putOrganizerCache = React.useCallback((pid, key, result) => {
    setOrganizerCache(prev => ({ ...prev, [`${pid}:${key}`]: result }));
  }, []);
  // S59 P3 — ouverture modale organisation depuis FilesInterface (bouton "Organiser avec l'IA")
  useEffect(() => {
    const handler = (e) => {
      if (!e.detail || e.detail.projectId === projectId) {
        setShowOrganizerModal(true);
      }
    };
    window.addEventListener('open-file-organizer', handler);
    return () => window.removeEventListener('open-file-organizer', handler);
  }, [projectId]);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [templatesVersion, setTemplatesVersion] = useState(0);
  const [helpMode, setHelpMode] = useState(false);
  const [editTemplateData, setEditTemplateData] = useState(null);
  const [showChatSearch, setShowChatSearch] = useState(false);
  const [searchHighlightId, setSearchHighlightId] = useState(null);
  const [showConsolidateModal, setShowConsolidateModal] = useState(false);
  const [editingMsgId, setEditingMsgId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [noteTitle, setNoteTitle] = useState('');
  const [noteContent, setNoteContent] = useState('');
  const analyzeGuardRef = useRef(false);
  const { sendStreamMessage, streamingContent, streamingImages, streamingDocuments, streamingActions, streamingRubriques, streamingDialecteMeta, currentStep, completedGroups, isStreaming, budgetAlert, sessionMessageCount, setSessionMessageCount, abort: abortStream } = useStreamChat();
  // Boutons d'action HITL (déterministes)
  const [dismissedActions, setDismissedActions] = useState(() => new Set());
  const [busyActionId, setBusyActionId] = useState(null);
  // Typewriter adaptatif : lisse l'affichage même quand les chunks SSE arrivent en bloc
  // (Sonnet thinking silencieux puis burst, batching Nginx, timeout fallback DB reload).
  const displayedStreamContent = useAdaptiveTypewriter(streamingContent, { tickMs: 16, maxStep: 30 });
  const { attachments, handlePaste, addFiles, addUrl, removeAttachment, updateDestination, clearAttachments } = usePasteHandler({ onError: (msg) => toast.warning(msg) });
  const handleFileAction = async (mode) => {
    if (isLoading || isStreaming) return;
    setIsLoading(true);

    const triggerMsg = {
      id: Date.now(),
      content: mode === 'analyze'
        ? 'Analyse compl\u00e8te des fichiers du projet...'
        : 'R\u00e9sum\u00e9 synth\u00e9tique des fichiers du projet...',
      isUser: true,
      timestamp: new Date(),
    };
    // Forcer streaming: false sur tous les messages précédents (anti-doublon)
    setChatMessages(prev => [...prev.map(m => m.streaming ? { ...m, streaming: false } : m), triggerMsg]);

    const aiMsgId = Date.now() + 1;
    setChatMessages(prev => [...prev, {
      id: aiMsgId, content: '', isUser: false, timestamp: new Date(), model: '', streaming: true
    }]);
    try {
      const result = await sendStreamMessage(`/api/projects/${projectId}/files/analyze/stream`, {
        mode,
        session_id: activeSessionId || null,
        forced_model: forcedModelRef.current || null,
      });

      if (result?.aborted) return;

      // Finaliser le message streaming
      setChatMessages(prev => prev.map(m =>
        m.id === aiMsgId ? {
          ...m,
          content: result?.content || m.content,
          streaming: false,
          model: result?.meta?.model_used || m.model,
          cost: result?.meta?.cost || m.cost,
        } : m
      ));
      if (result?.meta?.cost?.cost_usd) {
        setSessionCost(prev => prev + result.meta.cost.cost_usd);
        loadBudget();
      }

      setTimeout(() => { loadSessions(); }, 300);
    } catch (error) {
      setChatMessages(prev => prev.map(m =>
        m.id === aiMsgId ? { ...m, content: 'Erreur: ' + error.message, streaming: false } : m
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAnalyze = () => handleFileAction('analyze');
  const handleSummary = () => handleFileAction('summary');

  // S57 — Masquer UNIQUEMENT la FilesSidebar droite quand on est dans l'atelier Livre.
  //       Le header projet + la sidebar principale (Mon Espace / Projets) restent visibles.
  //       Pas de onSplitViewChange ici — c'est réservé au vrai mode plein écran preview.
  useEffect(() => {
    if (activeTab === 'book') {
      onBookWorkspaceChange?.(true);
    } else {
      onBookWorkspaceChange?.(false);
    }
  }, [activeTab, onBookWorkspaceChange]);

  // Session Greg — ouverture d'un projet avec prompt pré-rempli depuis ProfileDashboard
  // (sessionStorage one-shot). Crée une nouvelle session pour ne pas polluer l'existante.
  //
  // Session Fondateurs (modules II-V FD) — en plus du prompt, on gère :
  //   - profile_initial_prompt_auto_send → déclenche sendMessage dès que la session est prête
  //   - profile_initial_preview_url → fetch le HTML + ouvre le PreviewPane à droite
  useEffect(() => {
    if (!projectId) return;
    const prompt = sessionStorage.getItem('profile_initial_prompt');
    if (!prompt) return;
    // Verrou synchrone AVANT tout await : signale à loadSessions/handleSelectSession
    // qu'on pilote la session + les messages. Sans ça, loadSessions auto-sélectionne
    // la dernière session DB et écrase l'activeSessionId posé par handleNewSession,
    // virant du même coup le AI placeholder → stream perd sa cible.
    profileFlowPendingRef.current = true;
    const forcedModelFromProfile = sessionStorage.getItem('profile_initial_model');
    const systemAddendumFromProfile = sessionStorage.getItem('profile_initial_system_addendum');
    const autoSend = sessionStorage.getItem('profile_initial_prompt_auto_send') === 'true';
    const previewUrl = sessionStorage.getItem('profile_initial_preview_url');
    const promptKey = sessionStorage.getItem('profile_initial_prompt_key') || null;
    sessionStorage.removeItem('profile_initial_prompt');
    sessionStorage.removeItem('profile_initial_prompt_key');
    sessionStorage.removeItem('profile_initial_model');
    sessionStorage.removeItem('profile_initial_system_addendum');
    sessionStorage.removeItem('profile_initial_prompt_auto_send');
    sessionStorage.removeItem('profile_initial_preview_url');
    if (systemAddendumFromProfile) pendingSystemAddendumRef.current = systemAddendumFromProfile;
    (async () => {
      const token = localStorage.getItem('oracle_token');
      let reuseExisting = false;
      let targetSession = null;

      // Migration 074 — find-or-create par prompt_key (si fourni par la card/module).
      // Le backend renvoie { existing: true } si une session canonique existe déjà
      // pour ce prompt_key dans ce projet. Dans ce cas on rouvre la conv cachée
      // (messages DB) sans déclencher un nouveau call LLM.
      if (promptKey) {
        try {
          const resp = await fetch(`/api/projects/${projectId}/sessions`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ profile_prompt_key: promptKey }),
          });
          if (resp.ok) {
            const data = await resp.json();
            targetSession = data;
            reuseExisting = !!data?.existing;
          }
        } catch (e) { /* fallback: flow legacy ci-dessous */ }
      }

      if (targetSession?.id) {
        setActiveSessionId(targetSession.id);
        if (reuseExisting) {
          // Session canonique déjà utilisée : charger les messages DB et skip auto-send
          try {
            const msgsResp = await fetch(`/api/projects/${projectId}/sessions/${targetSession.id}/messages`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            if (msgsResp.ok) {
              const messages = await msgsResp.json();
              setChatMessages(messages.flatMap(m => {
                const ctx = m.context_used || {};
                return [
                  { id: m.id, content: m.message, isUser: true, timestamp: new Date(m.created_at), pasteMetadata: ctx.paste_metadata || undefined, authorId: m.user_id, authorName: m.author_full_name || m.author_username || null, authorIsSelf: m.user_id === user?.id },
                  { id: m.id + 0.5, content: m.response, isUser: false, timestamp: new Date(m.created_at), model: m.model_used, images: ctx.images || [], documents: ctx.documents || [] },
                ];
              }));
            }
          } catch (e) { /* silent */ }
        } else {
          // Nouvelle session taggée : canvas vierge, l'auto-send produira la réponse initiale
          setChatMessages([]);
        }
        // loadSessions aussi pour que la sidebar pills soit à jour
        try { loadSessions(); } catch {}
      } else {
        // Fallback legacy (pas de prompt_key, ou appel API échoué) :
        // réutiliser la dernière session vide si présente, sinon créer.
        let reuseEmpty = false;
        try {
          const resp = await fetch(`/api/projects/${projectId}/sessions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (resp.ok) {
            const sessions = await resp.json();
            if (Array.isArray(sessions) && sessions.length > 0) {
              const latest = sessions[0];
              const count = parseInt(latest.messages_count ?? latest.message_count ?? 0, 10);
              if (count === 0 && latest.id) {
                reuseEmpty = true;
                setActiveSessionId(latest.id);
                setChatMessages([]);
              }
            }
          }
        } catch { /* fallback: nouvelle session */ }
        if (!reuseEmpty) {
          try { await handleNewSession(); } catch { /* silent */ }
        }
      }

      setActiveTab('chat');
      if (forcedModelFromProfile) {
        forcedModelRef.current = forcedModelFromProfile; // synchrone : dispo pour l'auto-send immédiat
        setForcedModel(forcedModelFromProfile);
      }

      if (reuseExisting) {
        // Session cachée rouverte : pas d'auto-send, pas de prompt pré-rempli.
        // L'utilisateur voit directement la réponse initiale, peut poursuivre la
        // discussion librement. Le verrou profileFlow est libéré immédiatement.
        setChatInput('');
        pendingAutoSendRef.current = false;
        pendingSystemAddendumRef.current = null;
        profileFlowPendingRef.current = false;
      } else {
        setChatInput(prompt);
        if (autoSend) pendingAutoSendRef.current = true;
      }

      // Ouvrir le PreviewPane avec le HTML de présentation (fire-and-forget, non bloquant)
      if (previewUrl) {
        (async () => {
          try {
            const htmlResp = await fetch(previewUrl);
            if (!htmlResp.ok) return;
            const htmlString = await htmlResp.text();
            const filename = previewUrl.split('/').pop() || 'presentation.html';
            projShowPreviewRaw({ type: 'html', content: htmlString, filename });
          } catch (e) { /* silent — le chat fonctionne sans la preview */ }
        })();
      }
      // Note : pour une nouvelle session avec auto-send, profileFlowPendingRef
      // est libéré dans sendMessage (après guards, avant injection userMsg+AIplaceholder).
      // Pour une session cachée rouverte, il est libéré ci-dessus immédiatement.
    })();
  }, [projectId]);

  // Mode preview-only (card "Lire la présentation" — pas de prompt, juste le HTML) :
  // ouvre le PreviewPane avec le document + rétracte les 2 sidebars SANS passer en
  // plein écran (le haut — tabs, pills, header — reste visible pour naviguer vers
  // les autres cards). Rétraction gauche via event custom écouté par AppLayout,
  // rétraction droite (FilesSidebar) via onFocusToggle. Bouton PDF dans PreviewPane.
  useEffect(() => {
    if (!projectId) return;
    const prompt = sessionStorage.getItem('profile_initial_prompt');
    // Si un prompt est présent, le useEffect principal ci-dessus s'occupe du preview — on sort.
    if (prompt) return;
    const previewUrl = sessionStorage.getItem('profile_initial_preview_url');
    if (!previewUrl) return;
    const cinemaMode = sessionStorage.getItem('profile_initial_cinema_mode') === 'true';
    const pdfUrl = sessionStorage.getItem('profile_initial_pdf_url');
    sessionStorage.removeItem('profile_initial_preview_url');
    sessionStorage.removeItem('profile_initial_cinema_mode');
    sessionStorage.removeItem('profile_initial_pdf_url');
    (async () => {
      try {
        const htmlResp = await fetch(previewUrl);
        if (!htmlResp.ok) return;
        const htmlString = await htmlResp.text();
        const filename = previewUrl.split('/').pop() || 'presentation.html';
        projShowPreviewRaw({ type: 'html', content: htmlString, filename, pdfUrl: pdfUrl || null });
        if (cinemaMode) {
          // Sidebar latérale laissée pleinement ouverte pour permettre la navigation
          // entre cards. On rétracte uniquement la FilesSidebar droite pour libérer
          // la largeur de la présentation.
          onFocusToggle?.(true);
        }
      } catch (e) { /* silent — la card reste accessible via l'URL publique */ }
    })();
  }, [projectId]);

  // Sync ref ← state : couvre le changement manuel de modèle via le picker
  // (le profile flow pose la ref synchroniquement en amont pour l'auto-send).
  useEffect(() => { forcedModelRef.current = forcedModel; }, [forcedModel]);

  // Auto-send — déclenche sendMessage() une fois que chatInput est bien committé
  // dans le state React et qu'aucun stream/load n'est en cours. pendingAutoSendRef
  // est posé par l'useEffect précédent pour les modules II-V du template FD.
  useEffect(() => {
    if (!pendingAutoSendRef.current) return;
    if (!chatInput || !chatInput.trim()) return;
    if (isLoading || isStreaming) return;
    pendingAutoSendRef.current = false;
    // Micro-delay pour laisser l'UI se stabiliser (session, tab, forcedModel)
    const t = setTimeout(() => { try { sendMessage(); } catch (e) { /* silent */ } }, 150);
    return () => clearTimeout(t);
  }, [chatInput, isLoading, isStreaming]);

  // Analyse individuelle d'un fichier
  const handleFileAnalyze = async (fileId, fileName, mode = 'analyze') => {
    // Toujours basculer vers le chat, même si une analyse est déjà en cours
    if (activeTab !== 'chat') setActiveTab('chat');

    // Guard synchrone anti-doublon (React state pas assez rapide)
    if (isLoading || isStreaming || analyzeGuardRef.current) return;
    analyzeGuardRef.current = true;
    setIsLoading(true);

    const label = mode === 'analyze' ? 'Analyse' : 'Résumé';
    const triggerMsg = {
      id: Date.now(),
      content: `${label} du fichier "${fileName}"...`,
      isUser: true,
      timestamp: new Date(),
    };
    // Forcer streaming: false sur tous les messages précédents (anti-doublon)
    shouldScrollToUserMsg.current = true;
    setChatMessages(prev => [...prev.map(m => m.streaming ? { ...m, streaming: false } : m), triggerMsg]);

    const aiMsgId = Date.now() + 1;
    setChatMessages(prev => [...prev, {
      id: aiMsgId, content: '', isUser: false, timestamp: new Date(), model: '', streaming: true
    }]);
    try {
      // Contexte conversationnel : 5 derniers messages pour guider l'analyse
      const recentContext = chatMessages
        .filter(m => m.content && !m.streaming)
        .slice(-5)
        .map(m => ({ role: m.isUser ? 'user' : 'assistant', content: (m.content || '').substring(0, 1000) }));

      const result = await sendStreamMessage(`/api/projects/${projectId}/files/${fileId}/analyze/stream`, {
        mode,
        session_id: activeSessionId || null,
        forced_model: forcedModelRef.current || null,
        context_messages: recentContext.length > 0 ? recentContext : undefined,
      });

      if (result?.aborted) { analyzeGuardRef.current = false; return; }

      // Finaliser le message streaming
      setChatMessages(prev => prev.map(m =>
        m.id === aiMsgId ? {
          ...m,
          content: result?.content || m.content,
          streaming: false,
          model: result?.meta?.model_used || m.model,
          cost: result?.meta?.cost || m.cost,
        } : m
      ));
      if (result?.meta?.cost?.cost_usd) {
        setSessionCost(prev => prev + result.meta.cost.cost_usd);
      }

      setTimeout(() => { loadSessions(); }, 500);
    } catch (error) {
      setChatMessages(prev => prev.map(m =>
        m.id === aiMsgId ? { ...m, content: 'Erreur: ' + error.message, streaming: false } : m
      ));
    } finally {
      setIsLoading(false);
      analyzeGuardRef.current = false;
    }
  };
  const handleInsertTemplate = (text) => {
    setChatInput(prev => prev ? prev + '\n\n' + text : text);
  };
  const handleClear = async () => {
  if (await confirmModal('Vider la conversation projet ?')) {
    setChatMessages([]);
  }
};
  const handleBaseExpertise = () => console.log('Base expertise projet:', projectId);

  // Helper partagé B/C — relance la génération à partir d'un message user (optionnellement édité)
  // Workflow : truncate depuis ce message (soft-delete DB + retire UI) puis re-envoie en stream
  const rerunFrom = async (message, newContent = null) => {
    const content = (newContent ?? message.content ?? '').trim();
    if (!content) return;
    const convId = typeof message.id === 'number' ? message.id : parseInt(String(message.id).replace('_user', '').replace('_ai', ''));
    try {
      const token = localStorage.getItem('oracle_token');
      // 1. Truncate depuis ce message (soft-delete DB + UI locale)
      // On envoie AUSSI le timestamp en fallback : si convId est un Date.now() local (message
      // fraîchement envoyé, pas encore rechargé depuis la DB), le backend retombera sur le
      // timestamp pour trouver les messages à supprimer.
      const pivotIso = message.timestamp ? new Date(message.timestamp).toISOString() : null;
      await fetch(`/api/projects/${projectId}/conversations/truncate`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_conversation_id: convId, from_created_at: pivotIso, session_id: activeSessionId }),
      });
      const msgTimestamp = message.timestamp;
      setChatMessages(prev => prev.filter(m => new Date(m.timestamp) < new Date(msgTimestamp)));

      // 2. Ajouter la nouvelle paire user+IA locale (comme un nouveau message)
      const userMsg = { id: Date.now(), content, isUser: true, timestamp: new Date() };
      const aiMsgId = Date.now() + 1;
      shouldScrollToUserMsg.current = true;
      setChatMessages(prev => [
        ...prev,
        userMsg,
        { id: aiMsgId, content: '', isUser: false, timestamp: new Date(), model: '', streaming: true }
      ]);
      setIsLoading(true);

      // 3. Relancer via le même endpoint streaming que l'envoi normal
      const result = await sendStreamMessage(`/api/projects/${projectId}/chat/stream`, {
        message: content,
        enable_cm_model: cmActive,
        session_id: activeSessionId || null,
        forced_model: forcedModelRef.current || null,
      });

      if (result?.aborted) return;

      let finalContent = result?.content || '';
      const docs = result?.documents?.length > 0 ? result.documents : (result?.meta?.documents || []);
      for (const doc of docs) {
        if (doc.url && doc.filename && !finalContent.includes(`[ITRIIA_DOC:${doc.filename}`)) {
          finalContent += `\n\n[ITRIIA_DOC:${doc.filename}:${doc.url}:${doc.size || 0}]`;
        }
      }
      setChatMessages(prev => prev.map(m =>
        m.id === aiMsgId ? {
          ...m,
          content: finalContent,
          streaming: false,
          model: result?.meta?.model_used || m.model,
          cost: result?.meta?.cost || m.cost,
          images: result?.images?.length > 0 ? result.images : (result?.meta?.images || []),
          documents: docs,
        } : m
      ));
      if (result?.meta?.cost?.cost_usd) setSessionCost(prev => prev + result.meta.cost.cost_usd);
      setTimeout(() => loadSessions(), 300);
    } catch (e) {
      console.error('Erreur rerun:', e);
      toast.error('Erreur lors de la relance');
    } finally {
      setIsLoading(false);
    }
  };

  // Relancer : re-exécute une requête sans la modifier (bouton ↻ sur message user)
  const handleRerun = async (message) => {
    const confirmed = await confirmModal(
      'Relancer cette requête ? Les réponses suivantes seront remplacées.',
      { confirmText: 'Relancer', danger: false }
    );
    if (!confirmed) return;
    await rerunFrom(message);
  };

  // Edit message user : si le contenu a changé → régénération auto (truncate + rerun)
  const handleEditMessage = async (msgId) => {
    if (!editContent.trim()) return;
    const message = chatMessages.find(m => m.id === msgId);
    if (!message) return;
    const contentChanged = editContent.trim() !== (message.content || '').trim();

    if (!contentChanged) {
      setEditingMsgId(null);
      setEditContent('');
      return;
    }

    const confirmed = await confirmModal(
      'Régénérer la réponse avec le nouveau contenu ? Les messages suivants seront supprimés.',
      { confirmText: 'Régénérer', danger: false }
    );
    if (!confirmed) return;
    const newContent = editContent.trim();
    setEditingMsgId(null);
    setEditContent('');
    await rerunFrom(message, newContent);
  };

  // Truncate — supprimer un message et tous les suivants
  const handleTruncate = async (message) => {
    const confirmed = await confirmModal('Supprimer ce message et tous les suivants ?', {
      confirmText: 'Supprimer',
      danger: true,
    });
    if (!confirmed) return;
    const convId = typeof message.id === 'number' ? message.id : parseInt(String(message.id).replace('_user', '').replace('_ai', ''));
    // Fallback timestamp pour les messages fraîchement streamés qui n'ont pas encore l'ID DB
    const fromCreatedAt = message.timestamp ? new Date(message.timestamp).toISOString() : null;
    try {
      const token = localStorage.getItem('oracle_token');
      const resp = await fetch(`/api/projects/${projectId}/conversations/truncate`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ from_conversation_id: convId, from_created_at: fromCreatedAt, session_id: activeSessionId }),
      });
      const data = await resp.json();
      if (data.success) {
        // Supprimer localement tous les messages à partir du timestamp du message ciblé
        const msgTimestamp = message.timestamp;
        setChatMessages(prev => prev.filter(m => new Date(m.timestamp) < new Date(msgTimestamp)));
        toast.success(`${data.deleted_count} message(s) supprimé(s)`);
      }
    } catch (e) {
      console.error('Erreur truncate:', e);
      toast.error('Erreur suppression');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('oracle_token');
      await fetch(`/api/projects/${projectId}/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setChatMessages([]);
      }
      loadSessions();
    } catch (e) { console.error('Erreur suppression session:', e); }
  };

  // Session F+1 — Export ZIP markdown des sessions sélectionnées (archive perso, ne touche pas au serveur)
  const handleExportSessionsZip = async (sessionIds) => {
    if (!Array.isArray(sessionIds) || sessionIds.length === 0) return;
    const token = localStorage.getItem('oracle_token');
    const slug = (s) => String(s || 'sans-titre')
      .toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 60) || 'sans-titre';
    const fmt = (d) => {
      try {
        const dt = new Date(d);
        return dt.toLocaleDateString('fr-FR') + ' ' + dt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      } catch { return d; }
    };

    const zip = new JSZip();
    const errors = [];
    let successCount = 0;

    for (const sid of sessionIds) {
      try {
        const session = allSessions.find(s => s.id === sid) || sessions.find(s => s.id === sid);
        const title = session?.title || `Session ${sid}`;
        const created = session?.created_at;

        const r = await fetch(`/api/projects/${projectId}/sessions/${sid}/messages`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const messages = await r.json();

        const lines = [];
        lines.push(`# ${title}`);
        lines.push('');
        lines.push(`> Projet : ${projectData?.name || `#${projectId}`}` + (created ? ` · créée ${fmt(created)}` : ''));
        lines.push(`> ${messages.length} message(s) · exporté ${fmt(new Date())}`);
        lines.push('');
        lines.push('---');
        lines.push('');

        for (const m of messages) {
          const ts = m.created_at ? fmt(m.created_at) : '';
          if (m.message) {
            lines.push(`### ${ts} — Vous`);
            lines.push('');
            lines.push(m.message);
            lines.push('');
          }
          if (m.response) {
            const model = m.model_used ? ` · ${m.model_used}` : '';
            lines.push(`### ${ts} — ITRIIA${model}`);
            lines.push('');
            lines.push(m.response);
            lines.push('');
          }
          lines.push('---');
          lines.push('');
        }

        const filename = `${slug(title)}-${sid}.md`;
        zip.file(filename, lines.join('\n'));
        successCount++;
      } catch (e) {
        errors.push(`session ${sid} : ${e.message}`);
      }
    }

    if (successCount === 0) {
      toast.error('Export impossible : ' + (errors[0] || 'aucune session récupérée'));
      return;
    }

    // README index
    const readme = [
      '# Export ITRIIA — Conversations',
      '',
      `**Projet** : ${projectData?.name || `#${projectId}`}`,
      `**Sessions exportées** : ${successCount} / ${sessionIds.length}`,
      `**Date export** : ${fmt(new Date())}`,
      '',
      errors.length ? `## Erreurs (${errors.length})\n\n${errors.map(e => `- ${e}`).join('\n')}\n` : '',
      '## Note',
      '',
      'Cet export est une **archive personnelle** au format Markdown standard,',
      "compatible Notion, Obsidian, VSCode, ou tout éditeur Markdown. Le contenu",
      "n'a **pas** été retiré de la mémoire de l'IA — pour cela, utilisez le",
      'coffre-fort sur les messages clés depuis l\'interface chat.',
      '',
    ].filter(Boolean).join('\n');
    zip.file('README.md', readme);

    const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const projSlug = slug(projectData?.name || `projet-${projectId}`);
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `discussions-itriia-${projSlug}-${stamp}.zip`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);

    toast.success(`Export téléchargé : ${successCount} conversation(s)${errors.length ? ` · ${errors.length} erreur(s)` : ''}`);
  };

  const handleArchiveSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('oracle_token');
      await fetch(`/api/projects/${projectId}/sessions/${sessionId}/archive`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (activeSessionId === sessionId) {
        setActiveSessionId(null);
        setChatMessages([]);
      }
      loadSessions();
      loadAllSessions();
      toast.success('Conversation archivée');
    } catch (e) { console.error('Erreur archivage session:', e); }
  };

  const handleUnarchiveSession = async (sessionId) => {
    try {
      const token = localStorage.getItem('oracle_token');
      await fetch(`/api/projects/${projectId}/sessions/${sessionId}/archive`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });
      loadSessions();
      loadAllSessions();
      toast.success('Conversation désarchivée');
    } catch (e) { console.error('Erreur désarchivage session:', e); }
  };

  const handleRenameSession = async (sessionId, newTitle) => {
    try {
      const token = localStorage.getItem('oracle_token');
      await fetch(`/api/projects/${projectId}/sessions/${sessionId}/rename`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle })
      });
      loadSessions();
      loadAllSessions();
    } catch (e) { console.error('Erreur renommage session:', e); }
  };

  const loadAllSessions = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await fetch(`/api/projects/${projectId}/sessions?include_archived=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setAllSessions(await response.json());
      }
    } catch (e) { console.error('Erreur chargement all sessions:', e); }
  };

  const handleSaveNote = async () => {
    if (!noteContent.trim()) { toast.warning('Contenu requis'); return; }
    try {
      const token = localStorage.getItem('oracle_token');
      const filename = (noteTitle.trim() || `note-${Date.now()}`).replace(/[^a-zA-Z0-9\-_àâéèêëïîôùûüÿçÀÂÉÈ ]/g, '') + '.md';
      await fetch(`/api/projects/${projectId}/paste-as-file`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: noteTitle.trim() ? `# ${noteTitle.trim()}\n\n${noteContent}` : noteContent,
          filename,
          contentType: 'markdown',
          autoGenerated: false,
        }),
      });
      toast.success('Note sauvegardée');
      setShowNoteModal(false);
      setNoteTitle('');
      setNoteContent('');
      if (onFilesChanged) onFilesChanged();
    } catch (e) {
      toast.error('Erreur sauvegarde note');
    }
  };

  // S-I — Sauvegarde scroll chat pour restauration au retour d'onglet
  const savedChatScrollRef = useRef(0);

  const handleChatScroll = () => {
    const el = chatContainerRef.current;
    if (!el) return;
    // S57 — la logique d'affichage du bouton ⬇️ est désormais dans le composant
    // <ScrollToBottomButton containerRef={chatContainerRef} /> (hystérésis interne).
    // On garde uniquement la sauvegarde de scrollTop (savedChatScrollRef) ici.
    // Sauvegarder en continu pour restauration switch d'onglet
    savedChatScrollRef.current = el.scrollTop;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // S-I — Restaurer scroll quand on revient sur l'onglet chat
  useEffect(() => {
    if (activeTab !== 'chat') return;
    if (savedChatScrollRef.current <= 0) return;
    // 2 RAF : attendre que le DOM soit monté + premier paint
    const id1 = requestAnimationFrame(() => {
      const id2 = requestAnimationFrame(() => {
        const el = chatContainerRef.current;
        if (el) el.scrollTop = savedChatScrollRef.current;
      });
      return () => cancelAnimationFrame(id2);
    });
    return () => cancelAnimationFrame(id1);
  }, [activeTab]);

  const copyResponse = (content, msgId) => {
    navigator.clipboard.writeText(content);
    setCopiedMsgId(msgId);
    setTimeout(() => setCopiedMsgId(null), 2000);
  };

  // QW3 (S56) — Copier en texte brut (markdown strippé) pour coller dans
  // un mail / Word / Google Docs sans voir les ** ## ` etc. Le bouton
  // « Copier » par défaut copie déjà le markdown source — celui-ci en est
  // le complément « sans formatage ».
  const copyResponsePlain = (content, msgId) => {
    const plain = String(content || '')
      .replace(/```[a-zA-Z0-9]*\n([\s\S]*?)\n```/g, '$1')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/\*\*([^*]+)\*\*/g, '$1')
      .replace(/\*([^*\n]+)\*/g, '$1')
      .replace(/__([^_]+)__/g, '$1')
      .replace(/_([^_\n]+)_/g, '$1')
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/^>\s+/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '• ')
      .replace(/!\[([^\]]*)\]\([^)]+\)/g, '[image]')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
      .replace(/^\s*\|.*\|\s*$/gm, (line) => line.replace(/\|/g, '\t').trim())
      .replace(/^\s*[-:]+[\s\-:|]*$/gm, '')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
    navigator.clipboard.writeText(plain);
    setCopiedMsgId(msgId);
    setTimeout(() => setCopiedMsgId(null), 2000);
  };

  // Sauvegarder un message IA comme fichier projet
  const openSavePrompt = (content, msgId) => {
    // Pré-remplir avec les premiers mots du contenu
    const firstLine = content.replace(/[#*_\[\]]/g, '').trim().substring(0, 50).replace(/[^a-zA-Z0-9àâäéèêëïîôùûüçÀÂÄÉÈÊËÏÎÔÙÛÜÇ\s-]/g, '').trim();
    const suggested = firstLine.replace(/\s+/g, '-').toLowerCase().substring(0, 40) || 'reponse-itriia';
    setSaveFileName(suggested + '.md');
    setSavePromptMsgId(msgId);
  };

  const saveMessageAsFile = async (content, msgId) => {
    if (!saveFileName.trim()) return;
    setSavingMsgId(msgId);
    setSavePromptMsgId(null);
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await fetch(`/api/projects/${projectId}/paste-as-file`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          content,
          filename: saveFileName.trim(),
          contentType: 'markdown',
          autoGenerated: true
        })
      });
      if (response.ok) {
        const result = await response.json();
        const { toast } = await import('../ui/Toast');
        toast.success(`💾 Sauvegardé : ${result.file?.original_name || saveFileName}`);
        setSavedMsgIds(prev => new Set([...prev, msgId]));
        if (onFilesChanged) onFilesChanged();
      } else if (response.status === 409) {
        const { toast } = await import('../ui/Toast');
        toast.warning('Ce fichier existe déjà — change le nom pour sauvegarder');
        setSavePromptMsgId(msgId); // réouvrir le prompt pour changer le nom
      } else {
        const { toast } = await import('../ui/Toast');
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (err) {
      const { toast } = await import('../ui/Toast');
      toast.error(err.message);
    } finally {
      setSavingMsgId(null);
      setSaveFileName('');
    }
  };

  const loadBudget = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await fetch(`/api/projects/${projectId}/budget`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setProjectBudget(await res.json());
    } catch {}
  };

  // Phase B2 — Compteur bases d'expertise attachées pour le pill header
  const loadExpertiseBasesCount = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await fetch(`/api/expertise-bases/project/${projectId}/attachments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setExpertiseBasesCount(Array.isArray(data) ? data.length : 0);
      } else {
        setExpertiseBasesCount(0);
      }
    } catch { setExpertiseBasesCount(0); }
  };

  useEffect(() => {
    loadProjectData();
    loadBudget();
    loadExpertiseBasesCount();
    loadSessions().then(() => {
      // Le chargement des messages se fera via handleSelectSession quand les sessions seront chargées
    });
    loadAllSessions();
  }, [projectId]);

  // Changement de session cible depuis l'extérieur (clic "Dernières discussions" alors que le projet est déjà ouvert)
  useEffect(() => {
    if (initialSessionId && initialSessionId !== activeSessionId) {
      handleSelectSession(initialSessionId);
      onInitialSessionConsumed?.();
    }
  }, [initialSessionId]);

  // S51 — Auto-ouverture ConsolidateModal depuis hash #consolidate-{sid}
  // Déclenché depuis NotificationBell (suggestion auto L1). Nettoie le hash pour
  // éviter une réouverture au refresh.
  useEffect(() => {
    if (!sessions || sessions.length === 0) return;
    const match = (window.location.hash || '').match(/^#consolidate-(\d+)$/);
    if (!match) return;
    const sid = parseInt(match[1], 10);
    const target = sessions.find(s => s.id === sid);
    if (!target) {
      toast.warning('Session de consolidation introuvable dans ce projet.');
      try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch {}
      return;
    }
    const msgs = Number(target.messages_count) || 0;
    if (msgs < 10) {
      toast.info('Conversation trop courte pour être consolidée (10 messages minimum).');
      try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch {}
      return;
    }
    if (activeSessionId !== sid) {
      handleSelectSession(sid);
    }
    setShowConsolidateModal(true);
    try { window.history.replaceState(null, '', window.location.pathname + window.location.search); } catch {}
  }, [sessions]);

  const loadSessions = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await fetch(`/api/projects/${projectId}/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSessions(data);
        // Skip auto-select si un flow profile (clic card dashboard FD) pilote la session.
        // Sinon on écrase l'activeSessionId que le flow profile vient de poser via handleNewSession,
        // et le AI placeholder disparaît quand handleSelectSession re-fetch les messages DB.
        if (profileFlowPendingRef.current) {
          return;
        }
        // Lecture via ref pour esquiver la stale closure (cf. commentaire activeSessionIdRef
        // L.70+) : si handleNewSession vient de poser un id, le state React n'est pas
        // encore visible dans cette closure mais le ref, oui.
        if (data.length > 0 && !activeSessionIdRef.current) {
          // Si une session cible est demandée (clic depuis "Dernières discussions"), la privilégier
          const target = initialSessionId && data.find(s => s.id === initialSessionId)
            ? initialSessionId
            : data[0].id;
          setActiveSessionId(target);
          handleSelectSession(target);
          if (initialSessionId) onInitialSessionConsumed?.();
        } else if (data.length === 0) {
          // Pas de sessions — charger l'historique brut (anciennes conversations sans session)
          loadChatHistory();
        }
      }
    } catch (e) { console.error('Erreur sessions:', e); }
  };

  const saveProjectTitle = async (newTitle) => {
    if (!newTitle.trim() || newTitle.trim() === projectData?.name) {
      setEditingTitle(false);
      return;
    }
    try {
      const token = localStorage.getItem('oracle_token');
      const res = await fetch(`/api/projects/${projectId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newTitle.trim() })
      });
      if (res.ok) {
        setProjectData(prev => ({ ...prev, name: newTitle.trim() }));
      }
    } catch (e) { console.error('Erreur rename:', e); }
    setEditingTitle(false);
  };

  const handleNewSession = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await fetch(`/api/projects/${projectId}/sessions`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      if (response.ok) {
        const session = await response.json();
        profileFlowPendingRef.current = true;
        setActiveSessionId(session.id);
        // Refresh amont : fetch miroir DB des messages de la nouvelle session
        // (vide) AVANT que l'user puisse taper. Synchronise le state frontend
        // avec la source DB — sans ça, le 1er sendMessage poste un placeholder
        // streaming qui peut être wipe par une race avec un fetch tardif qui
        // arrive avec la vraie liste (vide) et écrase le state pendant le stream.
        // Cf. retour Soufari : "c'est avant qu'il faut rafraîchir, pas après".
        try {
          const msgsResp = await fetch(`/api/projects/${projectId}/sessions/${session.id}/messages`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const messages = msgsResp.ok ? await msgsResp.json() : [];
          setChatMessages(messages.flatMap(m => {
            const ctx = m.context_used || {};
            return [
              { id: m.id, content: m.message, isUser: true, timestamp: new Date(m.created_at), pasteMetadata: ctx.paste_metadata || undefined, authorId: m.user_id, authorName: m.author_full_name || m.author_username || null, authorIsSelf: m.user_id === user?.id },
              { id: m.id + 0.5, content: m.response, isUser: false, timestamp: new Date(m.created_at), model: m.model_used, images: ctx.images || [], documents: ctx.documents || [] },
            ];
          }));
        } catch { setChatMessages([]); }
        loadSessions();
      }
    } catch (e) { console.error('Erreur création session:', e); }
  };

  const handleSelectSession = async (sessionId) => {
    setActiveSessionId(sessionId);
    setSessionCost(0);
    setSessionMessageCount(0);
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await fetch(`/api/projects/${projectId}/sessions/${sessionId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const messages = await response.json();
        setChatMessages(messages.flatMap(m => {
          const ctx = m.context_used || {};
          return [
            { id: m.id, content: m.message, isUser: true, timestamp: new Date(m.created_at), pasteMetadata: ctx.paste_metadata || undefined, authorId: m.user_id, authorName: m.author_full_name || m.author_username || null, authorIsSelf: m.user_id === user?.id },
            { id: m.id + 0.5, content: m.response, isUser: false, timestamp: new Date(m.created_at), model: m.model_used, images: ctx.images || [], documents: ctx.documents || [] },
          ];
        }));
      }
    } catch (e) { console.error('Erreur chargement session:', e); }
  };

  // Refresh contextuel : recharge UNIQUEMENT les messages persistés de la session active.
  // Préserve : activeSessionId, scroll position, cost/count, input draft, attachments, mode, onglet.
  const [isRefreshingChat, setIsRefreshingChat] = useState(false);
  const refreshActiveChat = async () => {
    if (!activeSessionId || isRefreshingChat) return;
    setIsRefreshingChat(true);
    const container = chatContainerRef.current;
    const scrollTopBefore = container?.scrollTop ?? 0;
    const scrollHeightBefore = container?.scrollHeight ?? 0;
    const wasNearBottom = container
      ? (scrollHeightBefore - container.clientHeight - scrollTopBefore) < 80
      : false;
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await fetch(`/api/projects/${projectId}/sessions/${activeSessionId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const messages = await response.json();
        // Suspendre le comportement de scroll auto vers le message user pendant ce refresh
        shouldScrollToUserMsg.current = false;
        setChatMessages(messages.flatMap(m => {
          const ctx = m.context_used || {};
          return [
            { id: m.id, content: m.message, isUser: true, timestamp: new Date(m.created_at), pasteMetadata: ctx.paste_metadata || undefined, authorId: m.user_id, authorName: m.author_full_name || m.author_username || null, authorIsSelf: m.user_id === user?.id },
            { id: m.id + 0.5, content: m.response, isUser: false, timestamp: new Date(m.created_at), model: m.model_used, images: ctx.images || [], documents: ctx.documents || [] },
          ];
        }));
        // Restaurer la position de scroll après re-render
        requestAnimationFrame(() => {
          const c = chatContainerRef.current;
          if (!c) return;
          if (wasNearBottom) {
            c.scrollTop = c.scrollHeight;
          } else {
            const delta = c.scrollHeight - scrollHeightBefore;
            c.scrollTop = scrollTopBefore + delta;
          }
        });
        const { toast } = await import('../ui/Toast');
        toast.info('Conversation rafraîchie', 1500);
      }
    } catch (e) {
      console.error('Erreur refresh chat:', e);
      const { toast } = await import('../ui/Toast');
      toast.error('Erreur lors du rafraîchissement');
    } finally {
      setIsRefreshingChat(false);
    }
  };

  useEffect(() => {
    // S-I — Phase 1 : juste après envoi → user msg en haut, INSTANT, désactive
    if (shouldScrollToUserMsg.current && lastUserMsgRef.current && chatContainerRef.current) {
      const container = chatContainerRef.current;
      const msgEl = lastUserMsgRef.current;
      const containerRect = container.getBoundingClientRect();
      const msgRect = msgEl.getBoundingClientRect();
      // -16px de respiration au-dessus pour ne pas coller au bord
      const scrollTarget = container.scrollTop + (msgRect.top - containerRect.top) - 16;
      container.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'instant' });
      // Désactiver immédiatement — pas de re-scroll pendant les chunks
      shouldScrollToUserMsg.current = false;
      savedChatScrollRef.current = container.scrollTop;
      return;
    }
    // S-I — Phase 2 : pendant le streaming, l'utilisateur garde son scroll
    // (le contenu pousse en dessous mais on ne force pas le scroll en bas)
    if (isStreaming) return;
    // S-I — Phase 3 : autres updates (chargement session, refresh) → bottom
    scrollToBottom();
  }, [chatMessages, isStreaming]);

  // Écouter les demandes d'analyse depuis la FilesSidebar (via App.js)
  useEffect(() => {
    if (fileAnalyzeRequest?.fileId) {
      handleFileAnalyze(fileAnalyzeRequest.fileId, fileAnalyzeRequest.fileName, fileAnalyzeRequest.mode);
    }
  }, [fileAnalyzeRequest?.ts]);

  const loadProjectData = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await fetch(`/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setProjectData(data);
      }
    } catch (error) {
      console.error('Erreur chargement projet:', error);
      setProjectData({
        name: `Projet ${projectId}`,
        description: 'Projet collaboratif Oracle TRYA',
        type: 'development',
        status: 'active'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadChatHistory = async () => {
    try {
      const token = localStorage.getItem('oracle_token');
      const response = await fetch(`/api/projects/${projectId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const messages = await response.json();
        setChatMessages(Array.isArray(messages) ? messages : []);
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
      setChatMessages([]);
    }
  };

  const sendMessage = async () => {
    const hasText = chatInput.trim().length > 0;
    const hasAttachments = attachments.length > 0;
    if ((!hasText && !hasAttachments) || isLoading || isStreaming) return;

    // Libérer le verrou profileFlow : on entre effectivement dans la phase d'envoi,
    // les setChatMessages qui suivent (userMsg + AIplaceholder) ne peuvent plus être
    // écrasés par un loadSessions tardif car activeSessionId est déjà posé.
    profileFlowPendingRef.current = false;

    // Séparer attachments par destination
    const projectAttachments = attachments.filter(a => a.destination === 'project');
    const discussionAttachments = attachments.filter(a => a.destination === 'discussion');

    // Sauvegarder les fichiers "base documentaire" avant l'envoi
    const token = localStorage.getItem('oracle_token');
    for (const att of projectAttachments) {
      try {
        await fetch(`/api/projects/${projectId}/paste-as-file`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: att.isImage ? att.base64 : att.content,
            filename: att.name,
            contentType: att.isImage ? 'image' : att.type,
            autoGenerated: true,
          }),
        });
      } catch (e) { console.warn('Erreur sauvegarde fichier base doc:', e); }
    }
    if (projectAttachments.length > 0 && onFilesChanged) onFilesChanged();

    // Préparer les fichiers "discussion" pour le contexte inline
    const attachedFiles = discussionAttachments.map(a => ({
      filename: a.name,
      content: a.isImage ? a.base64 : a.content,
      type: a.type,
      size: a.size,
      isImage: a.isImage,
    }));

    const displayContent = hasText
      ? chatInput + (hasAttachments ? `\n[${attachments.length} fichier(s) joint(s)]` : '')
      : `[${attachments.length} fichier(s) joint(s)]`;

    // Metadata pour affichage compact des contenus collés (>500 chars)
    const pasteMetadata = discussionAttachments
      .filter(a => !a.isImage && a.content && a.content.length > 500)
      .map(a => ({
        filename: a.name,
        type: a.type,
        size: a.size,
        content_length: a.content.length,
        content_preview: a.content.substring(0, 300),
      }));

    const userMessage = {
      id: Date.now(),
      content: displayContent,
      isUser: true,
      timestamp: new Date(),
      pasteMetadata: pasteMetadata.length > 0 ? pasteMetadata : undefined,
    };

    // Forcer streaming: false sur tous les messages précédents (anti-doublon si erreur précédente)
    shouldScrollToUserMsg.current = true;
    setChatMessages(prev => [...prev.map(m => m.streaming ? { ...m, streaming: false } : m), userMessage]);
    const currentInput = hasText ? chatInput : (hasAttachments ? 'Voici le(s) fichier(s) joint(s). Analyse le contenu.' : '');
    setChatInput('');
    clearAttachments();
    setIsLoading(true);

    // Ajouter un message IA placeholder qui se remplira en streaming
    const aiMsgId = Date.now() + 1;
    setChatMessages(prev => [...prev, {
      id: aiMsgId, content: '', isUser: false, timestamp: new Date(), model: '', streaming: true
    }]);

    try {
      // Session Greg — consommer l'addendum système one-shot au premier envoi de la session
      const initialSystemAddendum = pendingSystemAddendumRef.current;
      pendingSystemAddendumRef.current = null;
      const result = await sendStreamMessage(`/api/projects/${projectId}/chat/stream`, {
        message: currentInput,
        enable_cm_model: cmActive,
        session_id: activeSessionId || null,
        forced_model: forcedModelRef.current || null,
        ...(helpMode ? { help_mode: true } : {}),
        ...(attachedFiles.length > 0 ? { attached_files: attachedFiles } : {}),
        ...(pasteMetadata.length > 0 ? { paste_metadata: pasteMetadata } : {}),
        ...(initialSystemAddendum ? { initial_system_addendum: initialSystemAddendum } : {}),
      });

      if (result?.aborted) return;

      // Finaliser le message streaming : injecter contenu + marqueurs documents + marquer comme terminé
      let finalContent = result?.content || '';
      // Injecter les marqueurs [ITRIIA_DOC:...] pour le composant GeneratedDocument
      const docs = result?.documents?.length > 0 ? result.documents : (result?.meta?.documents || []);
      for (const doc of docs) {
        if (doc.url && doc.filename && !finalContent.includes(`[ITRIIA_DOC:${doc.filename}`)) {
          finalContent += `\n\n[ITRIIA_DOC:${doc.filename}:${doc.url}:${doc.size || 0}]`;
        }
      }
      setChatMessages(prev => prev.map(m =>
        m.id === aiMsgId ? {
          ...m,
          content: finalContent,
          streaming: false,
          model: result?.meta?.model_used || m.model,
          cost: result?.meta?.cost || m.cost,
          images: result?.images?.length > 0 ? result.images : (result?.meta?.images || []),
          documents: docs,
          actions: result?.actions || [],
          actionRubriques: result?.rubriques || [], // sélecteur rubrique HITL (persiste avec les boutons)
          dialecte_meta: result?.dialecteMeta || null, // S55 P1 : conserve le chip après stream end
          isHelp: result?.meta?.help_mode || false,
        } : m
      ));
      if (result?.meta?.cost?.cost_usd) {
        setSessionCost(prev => prev + result.meta.cost.cost_usd);
      }

      // 1er message d'une session : activeSessionId est encore nul. Sans ça, le
      // loadSessions ci-dessous voit !activeSessionIdRef.current → auto-select →
      // handleSelectSession refetch les messages depuis la DB (qui ne portent JAMAIS
      // `actions`, éphémères à token) → les boutons d'action HITL s'effacent juste
      // après s'être affichés. L'event `done` porte déjà session_id : on le pose (ref
      // en immédiat pour esquiver la stale closure du setTimeout) → loadSessions skip
      // le refetch et message.actions (déjà committé ci-dessus) survit.
      if (!activeSessionId && result?.meta?.session_id) {
        activeSessionIdRef.current = result.meta.session_id;
        setActiveSessionId(result.meta.session_id);
      }

      // Rafraîchir la liste des sessions (pas les messages — ils sont déjà à jour)
      setTimeout(() => { loadSessions(); }, 300);
    } catch (error) {
      const msg = error?.message || 'Erreur inconnue';
      const isTimeout = /ne répond plus|timeout|idle_timeout|hard_timeout/i.test(msg);
      if (isTimeout) {
        toast.error('Le modèle ne répond plus, essayez un autre modèle');
      } else {
        toast.error(msg);
      }
      setChatMessages(prev => prev.map(m =>
        m.id === aiMsgId ? { ...m, content: 'Erreur: ' + msg, streaming: false } : m
      ));
    } finally {
      setIsLoading(false);
    }
  };

  // Boutons d'action HITL. execute = exécution DÉTERMINISTE du payload figé via /invoke
  // (aucun re-prompt LLM → zéro ré-interprétation) ; dismiss = masquer localement.
  const handleActionClick = async (item, message) => {
    if (!item) return;
    if (item.kind === 'dismiss') {
      setDismissedActions(prev => new Set(prev).add(message.id));
      return;
    }
    if (item.kind === 'execute') {
      setBusyActionId(item.id);
      try {
        const auth = localStorage.getItem('oracle_token');
        // Le bouton ne porte qu'un token ; le backend résout le payload exact (publication)
        // côté serveur depuis pendingActions → exécution déterministe, event SSE resté tiny.
        const res = await fetch(`/api/projects/${projectId}/actions/execute`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${auth}`, 'Content-Type': 'application/json' },
          // item.rubrique = rubrique choisie dans le sélecteur HITL (undefined → omise par
          // JSON.stringify → branche legacy/serveur garde celle figée dans le token).
          body: JSON.stringify({ token: item.token, actif: item.actif, rubrique: item.rubrique }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.ok) {
          toast.success(item.done_label || 'Action effectuée ✓');
          setDismissedActions(prev => new Set(prev).add(message.id));
        } else {
          toast.error(data.body?.error || data.error || `Échec de l'action (${res.status})`);
        }
      } catch (e) {
        toast.error('Erreur réseau');
      } finally {
        setBusyActionId(null);
      }
    }
  };

  // S56/S57 — Onglet "Livre" conditionnel : feature flag com_writer + book_id présent
  const [featureFlags, setFeatureFlags] = useState(() => window.__itriiaFeatureFlags || null);
  useEffect(() => {
    const onReady = (e) => setFeatureFlags(e.detail || null);
    window.addEventListener('itriia-features-ready', onReady);
    return () => window.removeEventListener('itriia-features-ready', onReady);
  }, []);
  const comWriterEnabled = !featureFlags || featureFlags.com_writer !== false;
  const hasBook = !!projectData?.book_id;

  // Family P2c — Onglet Famille conditionnel : flag backend + foyer existant sur ce projet
  // Le fetch est lancé indépendamment du flag pour éviter un blocage UX au premier render
  // (le flag arrive via event asynchrone). Si le backend désactive le module, la route renvoie 503
  // → catch silencieux → hasFamily reste false.
  const familyModuleEnabled = featureFlags?.family_module === true;
  const [hasFamily, setHasFamily] = useState(false);
  useEffect(() => {
    if (!projectId) { setHasFamily(false); return; }
    let cancelled = false;
    getHousehold(projectId)
      .then((r) => { if (!cancelled) setHasFamily(!!r?.household); })
      .catch(() => { if (!cancelled) setHasFamily(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  // Pilot Mutation + Cockpit Média : onglets conditionnels selon les connecteurs actifs.
  const [hasWpSource, setHasWpSource] = useState(false);
  const [hasMediaConnector, setHasMediaConnector] = useState(false);
  useEffect(() => {
    if (!projectId) {
      setHasWpSource(false);
      setHasMediaConnector(false);
      return;
    }
    let cancelled = false;
    const token = localStorage.getItem('oracle_token');
    fetch(`/api/connectors/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : { connectors: [] }))
      .then(d => {
        if (cancelled) return;
        const active = (d?.connectors || []).filter(c => c.status === 'active');
        setHasWpSource(active.some(c => c.connector_code === 'wordpress_source'));
        setHasMediaConnector(active.some(c => ['wordpress_source', 'site_internet'].includes(c.connector_code)));
      })
      .catch(() => {
        if (!cancelled) {
          setHasWpSource(false);
          setHasMediaConnector(false);
        }
      });
    return () => { cancelled = true; };
  }, [projectId]);

  // S57-F — Récupère le genre du livre pour adapter les labels (onglet, boutons)
  const [bookGenre, setBookGenre] = useState(null);
  useEffect(() => {
    if (!projectData?.book_id) { setBookGenre(null); return; }
    getBook(projectData.book_id).then(r => setBookGenre(r?.book?.genre || 'essai')).catch(() => setBookGenre('essai'));
  }, [projectData?.book_id]);
  const docTerms = getDocTerms(bookGenre);

  const tabs = [
    { id: 'chat', label: 'Chat Projet', icon: MessageSquare },
    ...(hasBook && comWriterEnabled ? [{ id: 'book', label: 'Atelier Rédaction', icon: BookOpen }] : []),
    ...(hasFamily ? [{ id: 'family', label: 'Famille', icon: Home }] : []),
    ...(hasMediaConnector ? [{ id: 'media', label: 'Média', icon: Newspaper }] : []),
    ...(hasWpSource ? [{ id: 'pilot-mutation', label: 'Pilot Mutation', icon: Database }] : []),
    { id: 'files', label: 'Fichiers', icon: Upload },
    { id: 'expertise', label: 'Bases d\'Expertise', icon: BookMarked },
    { id: 'team', label: 'Équipe', icon: Users },
    { id: 'trans-memory', label: 'Meta Projets Memory', icon: Database },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'overview', label: 'Paramètres', icon: FileText }
  ];

  // Limiter l'affichage initial de l'historique
  const displayedMessages = showAllHistory
    ? chatMessages
    : chatMessages.slice(-15); // Afficher seulement les 15 derniers

  // Détection projet partagé (invité) — Session 35
  const currentUserId = (() => {
    try {
      const token = localStorage.getItem('oracle_token');
      if (!token) return null;
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id;
    } catch { return null; }
  })();
  const isGuest = !!(projectData?.creator_id && currentUserId && projectData.creator_id !== currentUserId);

  if (loading) {
    return (
      <div className="p-6 min-h-screen flex items-center justify-center" style={{ color: 'var(--text)' }}>
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-yellow-400 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>Chargement projet Oracle TRYA...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      padding: isFullScreen ? '0' : '24px',
      height: '100%',
      overflow: 'auto',
      background: isGuest
        ? 'linear-gradient(180deg, var(--shared-tint-strong) 0%, var(--shared-tint) 240px, transparent 520px)'
        : undefined,
      borderTop: isGuest ? '2px solid var(--shared-border)' : undefined,
    }}>
      {/* Header projet — caché en plein écran, sticky pour rester visible au scroll */}
      <div style={{
        display: isFullScreen ? 'none' : 'flex',
        alignItems: 'center',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        background: 'var(--main-bg)',
        marginLeft: '-24px',
        marginRight: '-24px',
        paddingLeft: '24px',
        paddingRight: '24px',
        paddingTop: '8px',
        paddingBottom: '12px',
        marginBottom: '12px',
        borderBottom: '1px solid var(--topbar-border)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      }}>
        <button
          onClick={onBack}
          style={{
            background: 'none',
            border: '1px solid var(--card-border)',
            borderRadius: '6px',
            padding: '8px 12px',
            marginRight: '16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'var(--text)'
          }}
        >
          <ArrowLeft size={16} />
          Retour
        </button>
        {editingTitle ? (
          <input
            autoFocus
            value={editTitle}
            onChange={e => setEditTitle(e.target.value)}
            onBlur={() => saveProjectTitle(editTitle)}
            onKeyDown={e => {
              if (e.key === 'Enter') saveProjectTitle(editTitle);
              if (e.key === 'Escape') setEditingTitle(false);
            }}
            style={{
              fontSize: '24px', fontWeight: 'bold', color: 'var(--text)',
              border: 'none', borderBottom: '2px solid #2563eb',
              outline: 'none', background: 'transparent', padding: '0 4px',
              fontFamily: 'inherit', minWidth: '200px'
            }}
          />
        ) : (
          <div
            onClick={() => { setEditTitle(projectData?.name || ''); setEditingTitle(true); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              cursor: 'pointer', padding: '0 4px',
              borderBottom: '2px solid transparent',
              transition: 'border-color 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderBottomColor = 'var(--card-border)'; e.currentTarget.querySelector('.edit-icon').style.opacity = '0.6'; }}
            onMouseLeave={e => { e.currentTarget.style.borderBottomColor = 'transparent'; e.currentTarget.querySelector('.edit-icon').style.opacity = '0'; }}
            title="Cliquez pour modifier le nom du projet"
          >
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--text)', margin: 0 }}>
              {projectData?.name || `Projet ${projectId}`}
            </h2>
            <Pencil className="edit-icon" size={14} style={{ color: 'var(--text-secondary)', opacity: 0, transition: 'opacity 0.2s' }} />
          </div>
        )}

        {/* Badge projet partagé confidentiel (invité) */}
        {isGuest && (
          <div
            onClick={e => e.stopPropagation()}
            title="Projet partagé — accès invité · Ne diffusez pas ce contenu à l'extérieur. Seul le créateur peut le repartager."
            style={{
              marginLeft: '12px',
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              padding: '5px 12px', borderRadius: '20px',
              background: 'linear-gradient(135deg, rgba(139,92,246,0.16), rgba(139,92,246,0.08))',
              border: '1px solid var(--shared-border)',
              color: 'var(--shared-accent)',
              fontSize: '11px', fontWeight: 700, letterSpacing: '0.4px',
              textTransform: 'uppercase',
              boxShadow: '0 0 12px rgba(139,92,246,0.18)',
              cursor: 'help',
            }}
          >
            <Lock size={12} strokeWidth={2.5} />
            Confidentiel
            <span style={{
              fontSize: '9px', fontWeight: 600, opacity: 0.75,
              textTransform: 'none', letterSpacing: 0,
              paddingLeft: '6px', marginLeft: '2px',
              borderLeft: '1px solid rgba(139,92,246,0.3)'
            }}>Invité</span>
          </div>
        )}
       
        {/* Toggle CM dans le header du projet */}
       <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px' }}>

  {/* Pill bases d'expertise (Phase B2) */}
  {expertiseBasesCount > 0 && (
    <div
      onClick={() => setActiveTab('overview')}
      title={`${expertiseBasesCount} base(s) d'expertise attachée(s) — Cliquer pour gérer`}
      style={{
        display: 'flex', alignItems: 'center', gap: '6px',
        padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
        background: 'rgba(139, 92, 246, 0.10)',
        border: '1px solid rgba(139, 92, 246, 0.28)',
        fontSize: '11px', fontWeight: '600', color: '#8b5cf6',
        transition: 'all 0.15s ease',
      }}
    >
      <span style={{ fontSize: '12px' }}>📚</span>
      {expertiseBasesCount} base{expertiseBasesCount > 1 ? 's' : ''}
    </div>
  )}

  {/* Pastille(s) site(s) connecté(s) — un badge vert par site, tous moteurs confondus
      (publication site_internet ET/OU source wordpress_source, regroupés par host). */}
  <ConnectedSiteBadge projectId={projectId} onClick={() => setActiveTab('overview')} />

  {/* Pill budget */}
  {projectBudget?.configured && (() => {
    const pct = projectBudget.percent || 0;
    const color = pct >= 100 ? '#ef4444' : pct >= 80 ? '#f59e0b' : '#10b981';
    const bg = pct >= 100 ? 'rgba(239,68,68,0.1)' : pct >= 80 ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)';
    const border = pct >= 100 ? 'rgba(239,68,68,0.25)' : pct >= 80 ? 'rgba(245,158,11,0.25)' : 'rgba(16,185,129,0.25)';
    return (
      <div
        onClick={() => setActiveTab('overview')}
        title={`$${projectBudget.spent_usd?.toFixed(4)} / $${projectBudget.budget_usd?.toFixed(2)} — Cliquer pour configurer`}
        style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          padding: '4px 10px', borderRadius: '20px', cursor: 'pointer',
          background: bg, border: `1px solid ${border}`,
          fontSize: '11px', fontWeight: '600', color,
          transition: 'all 0.3s ease',
        }}
      >
        <div style={{
          width: '8px', height: '8px', borderRadius: '50%', background: color,
          boxShadow: pct >= 80 ? `0 0 6px ${color}` : 'none',
          animation: pct >= 100 ? 'pulse 1.5s infinite' : 'none',
        }} />
        {pct}%
        <span style={{ fontWeight: '400', opacity: 0.8 }}>
          ${projectBudget.spent_usd?.toFixed(3)}
        </span>
      </div>
    );
  })()}

  {cmActive && (
    <span style={{ fontSize: '12px', color: '#1D4ED8', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#1D4ED8', boxShadow: '0 0 6px rgba(29,78,216,0.4)' }} />
      Expertise
    </span>
  )}
</div>
      </div>

      {/* Description projet : masquée en vue chat (redondante une fois dans le projet) — reste éditable dans l'onglet Paramètres */}
      <div style={{ borderBottom: '1px solid var(--topbar-border)', marginBottom: '24px', display: isFullScreen ? 'none' : 'block' }}>
        <style>{`
          .itriia-tab {
            position: relative;
            background: rgba(127,127,160,0.04);
            transition: background 0.18s ease, color 0.18s ease;
          }
          body.dark .itriia-tab { background: rgba(148,163,184,0.05); }
          .itriia-tab:hover:not(.is-active) {
            background: rgba(127,127,160,0.10);
            color: var(--text) !important;
          }
          body.dark .itriia-tab:hover:not(.is-active) {
            background: rgba(148,163,184,0.10);
          }
          .itriia-tab.is-active {
            background: var(--pill-soft-bg-hover);
            color: var(--pill-soft-text);
          }
          body.dark .itriia-tab.is-active {
            background: var(--pill-soft-bg-hover);
            color: var(--pill-soft-text);
          }
          .itriia-tab::after {
            content: '';
            position: absolute; left: 0; right: 0; bottom: -1px;
            height: 2px; background: var(--pill-soft-icon); border-radius: 2px 2px 0 0;
            transform: scaleX(0); transform-origin: center;
            transition: transform 0.2s ease;
          }
          body.dark .itriia-tab::after { background: var(--pill-soft-icon); }
          .itriia-tab.is-active::after { transform: scaleX(1); }
          .itriia-tab:hover:not(.is-active) .itriia-tab-icon { opacity: 1; }
          .itriia-tab-icon { opacity: 0.72; transition: opacity 0.2s ease; display: inline-flex; }
          .itriia-tab.is-active .itriia-tab-icon { opacity: 1; }
        `}</style>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'stretch' }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`itriia-tab${active ? ' is-active' : ''}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '7px',
                  padding: '10px 14px',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  color: active ? undefined : 'var(--text-secondary)',
                  fontWeight: active ? 600 : 500,
                  fontSize: '13px',
                  letterSpacing: '-0.01em',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                <span className="itriia-tab-icon"><Icon size={15} strokeWidth={active ? 2.2 : 1.75} /></span>
                {tab.label}
              </button>
            );
          })}
          {/* Boutons Split + Plein écran — icon-only gris, alignés header sobre */}
          {activeTab === 'chat' && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '2px' }}>
            <button
              onClick={() => {
                const next = !projPreviewOpen;
                if (next) { projShowPreview(projPreviewContent); } else { projClosePreview(); onSplitViewChange?.(false); }
              }}
              title={projPreviewOpen ? 'Fermer le split view' : 'Ouvrir le split view'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '26px', height: '26px', padding: 0,
                background: projPreviewOpen ? 'rgba(29,78,216,0.12)' : 'transparent',
                border: 'none', borderRadius: '6px', cursor: 'pointer',
                color: projPreviewOpen ? '#3B82F6' : 'var(--text-secondary)',
                transition: 'all 0.15s ease',
              }}
            >
              <PanelLeft size={14} />
            </button>
            <button
              onClick={() => onFocusToggle?.(!focusMode)}
              title={focusMode ? 'Sortir du plein écran' : 'Plein écran — masquer la sidebar fichiers'}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: '26px', height: '26px', padding: 0,
                background: focusMode ? 'rgba(16,185,129,0.12)' : 'transparent',
                border: 'none', borderRadius: '6px', cursor: 'pointer',
                color: focusMode ? '#10b981' : 'var(--text-secondary)',
                transition: 'all 0.15s ease',
              }}
            >
              {focusMode ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>
          )}
        </div>
      </div>

      {/* Contenu des onglets — hauteur augmentée en vue chat (description masquée) */}
      <div style={{ height: isFullScreen ? '100vh' : `calc(100vh - ${activeTab === 'chat' ? 230 : 280}px)` }}>
        {activeTab === 'chat' && (
          <SplitChatView
            mode="manual"
            isOpen={projPreviewOpen}
            onToggle={() => { projClosePreview(); onSplitViewChange?.(false); }}
            onFullScreenChange={(fs) => onSplitViewChange?.(fs)}
            previewContent={projPreviewContent}
            projects={projects}
            currentProjectId={projectId}
            onSaveAsFile={async (content, filename, targetProjectId) => {
              const token = localStorage.getItem('oracle_token');
              const tid = targetProjectId || projectId;
              const isSameProject = String(tid) === String(projectId);
              const targetName = !isSameProject ? (projects.find(p => String(p.id) === String(tid))?.name || `projet #${tid}`) : null;
              const response = await fetch(`/api/projects/${tid}/paste-as-file`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ content, filename, contentType: 'markdown', autoGenerated: true })
              });
              if (response.ok) {
                const result = await response.json();
                const { toast } = await import('../ui/Toast');
                const msg = isSameProject
                  ? `💾 Sauvegardé : ${result.file?.original_name || filename}`
                  : `📤 Envoyé vers ${targetName} : ${result.file?.original_name || filename}`;
                toast.success(msg);
                if (isSameProject && onFilesChanged) onFilesChanged();
              } else if (response.status === 409) {
                const { toast } = await import('../ui/Toast');
                toast.warning('Ce fichier existe déjà — change le nom');
                throw new Error('duplicate');
              } else {
                const { toast } = await import('../ui/Toast');
                toast.error('Erreur lors de la sauvegarde');
                throw new Error('save failed');
              }
            }}
          >
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            {/* Pills conversations + bouton recherche */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <ConversationPills
                  sessions={sessions}
                  activeSessionId={activeSessionId}
                  onSelectSession={handleSelectSession}
                  onNewSession={handleNewSession}
                  onArchiveSession={handleArchiveSession}
                  onOpenDrawer={() => { loadAllSessions(); setDrawerOpen(true); }}
                  messageCount={sessionMessageCount || chatMessages.length}
                />
              </div>
              <button
                onClick={refreshActiveChat}
                disabled={!activeSessionId || isRefreshingChat}
                title="Rafraîchir la conversation (recharge les messages sans perdre le contexte)"
                style={{
                  background: 'none', border: 'none', borderRadius: '6px', padding: '6px',
                  cursor: (!activeSessionId || isRefreshingChat) ? 'not-allowed' : 'pointer',
                  color: 'var(--text-secondary)',
                  opacity: (!activeSessionId || isRefreshingChat) ? 0.5 : 1,
                  flexShrink: 0, transition: 'all 0.15s',
                  display: 'flex', alignItems: 'center',
                }}
                onMouseEnter={e => { if (!isRefreshingChat) e.currentTarget.style.color = '#3b82f6'; }}
                onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; }}
              >
                <RefreshCw size={14} style={{ animation: isRefreshingChat ? 'spin 0.8s linear infinite' : 'none' }} />
              </button>
              {(() => {
                const effectiveCount = sessionMessageCount || chatMessages.length;
                const canConsolidate = !!activeSessionId && effectiveCount >= 10;
                const title = !activeSessionId
                  ? 'Sélectionne une conversation'
                  : effectiveCount < 10
                    ? `Au moins 10 messages nécessaires (actuellement ${effectiveCount})`
                    : 'Consolider cette conversation — transforme le fil en synthèse structurée';
                return (
                  <button
                    onClick={() => { if (canConsolidate) setShowConsolidateModal(true); }}
                    disabled={!canConsolidate}
                    title={title}
                    style={{
                      background: 'none', border: 'none', borderRadius: '6px', padding: '6px',
                      cursor: canConsolidate ? 'pointer' : 'not-allowed',
                      color: canConsolidate ? 'var(--text-secondary)' : 'var(--text-muted)',
                      opacity: canConsolidate ? 1 : 0.4,
                      flexShrink: 0, transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center',
                    }}
                    onMouseEnter={e => { if (canConsolidate) e.currentTarget.style.color = '#3b82f6'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = canConsolidate ? 'var(--text-secondary)' : 'var(--text-muted)'; }}
                  >
                    <GitCommit size={14} />
                  </button>
                );
              })()}
              {/* S49 — Toggle Suspendre la mémoire */}
              <MemoryScopeToggle
                projectId={projectId}
                sessionId={activeSessionId}
                currentScope={allSessions.find(s => s.id === activeSessionId)?.memory_scope || 'persistent'}
                disabled={!activeSessionId}
                onScopeChanged={(newScope) => {
                  setAllSessions(prev => prev.map(s => s.id === activeSessionId ? { ...s, memory_scope: newScope } : s));
                  // Recharger la liste sessions (pour refléter les counts après purge/reindex)
                  if (typeof loadAllSessions === 'function') loadAllSessions();
                }}
              />
              <button
                onClick={() => setShowChatSearch(prev => !prev)}
                title="Rechercher dans la conversation (Ctrl+F)"
                style={{
                  background: showChatSearch ? 'rgba(29,78,216,0.15)' : 'none',
                  border: 'none', borderRadius: '6px', padding: '6px',
                  cursor: 'pointer', color: showChatSearch ? '#3b82f6' : 'var(--text-secondary)',
                  flexShrink: 0, transition: 'all 0.15s',
                }}
              >
                <Search size={14} />
              </button>
            </div>
            {/* Drawer gestion conversations */}
            <ConversationDrawer
              isOpen={drawerOpen}
              onClose={() => setDrawerOpen(false)}
              sessions={allSessions}
              activeSessionId={activeSessionId}
              onSelectSession={handleSelectSession}
              onArchiveSession={handleArchiveSession}
              onUnarchiveSession={handleUnarchiveSession}
              onDeleteSession={handleDeleteSession}
              onRenameSession={handleRenameSession}
              onNewSession={handleNewSession}
              onExportZip={handleExportSessionsZip}
            />
            {/* Recherche dans le chat */}
            <ChatSearch
              messages={displayedMessages}
              isOpen={showChatSearch}
              onClose={() => { setShowChatSearch(false); setSearchHighlightId(null); }}
              onHighlight={setSearchHighlightId}
            />
            {/* Zone messages avec scroll */}
            <div
              ref={chatContainerRef}
              onScroll={handleChatScroll}
              style={{
              flex: 1,
              overflowY: 'scroll',
              overflowX: 'hidden',
              background: 'var(--input-bg)',
              borderRadius: '12px',
              padding: '20px 16px 16px',
              marginTop: '12px',
              marginBottom: '16px',
              position: 'relative'
            }}>
              {/* Bouton "Voir plus" si historique tronqué */}
              {!showAllHistory && chatMessages.length > 15 && (
                <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                  <button
                    onClick={() => setShowAllHistory(true)}
                    style={{
                      background: 'var(--card-bg)',
                      border: '1px solid var(--card-border)',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      cursor: 'pointer'
                    }}
                  >
                    Voir les {chatMessages.length - 15} messages précédents
                  </button>
                </div>
              )}

              {displayedMessages.length === 0 ? (
                <div style={{ maxWidth: '520px', margin: '32px auto', padding: '24px', background: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--card-border)' }}>
                  <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                    <MessageSquare size={36} style={{ display: 'inline', color: '#2563eb', marginBottom: '8px' }} />
                    <h3 style={{ fontSize: '16px', fontWeight: '700', color: 'var(--text)', marginBottom: '4px' }}>
                      Nouvelle conversation
                    </h3>
                    <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      {projectData?.name || 'Projet'}
                    </p>
                  </div>

                  {/* Statut mémoire */}
                  <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: '8px', padding: '12px 14px', marginBottom: '14px', fontSize: '13px' }}>
                    <div style={{ fontWeight: '600', color: '#22c55e', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e', display: 'inline-block' }}></span>
                      Mémoire Persistante active
                    </div>
                    <div style={{ color: 'var(--text-secondary)' }}>
                      {sessions.length > 1
                        ? `${sessions.length - 1} conversation${sessions.length > 2 ? 's' : ''} précédente${sessions.length > 2 ? 's' : ''} mémorisée${sessions.length > 2 ? 's' : ''} • Le contexte est transmis automatiquement.`
                        : 'Première conversation de ce projet. Le contexte sera construit au fil des échanges.'}
                    </div>
                  </div>

                  {/* Lien session précédente */}
                  {sessions.length > 1 && (
                    <button
                      onClick={() => handleSelectSession(sessions[1].id)}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', color: 'var(--text)', textAlign: 'left', marginBottom: '14px', transition: 'background 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--card-hover)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--card-bg)'}
                    >
                      <span style={{ fontSize: '16px' }}>💬</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500' }}>{sessions[1].title || 'Conversation précédente'}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{sessions[1].messages_count || 0} messages</div>
                      </div>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Voir →</span>
                    </button>
                  )}

                  {/* Guide */}
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                    <strong style={{ color: 'var(--text-secondary)' }}>Guide rapide :</strong> Posez une question, ITRIIA répondra avec le contexte du projet. Les fichiers uploadés sont automatiquement intégrés. {cmActive ? '🔮 Base Expertise Métier activée.' : '📝 Mode standard.'}
                  </div>
                </div>
              ) : (
                displayedMessages.map((message, idx) => {
                  // Trouver le dernier message user pour y attacher la ref de scroll
                  const lastUserIdx = displayedMessages.map((m, i) => m.isUser ? i : -1).filter(i => i >= 0).pop();
                  const isLastUser = message.isUser && idx === lastUserIdx;
                  const isLastMessage = idx === displayedMessages.length - 1;
                  return (
                  <div key={message.id} id={`msg-${message.id}`} ref={isLastUser ? lastUserMsgRef : (message.streaming ? streamingMsgRef : null)} style={{
                    display: 'flex',
                    justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                    marginBottom: '16px',
                    ...(searchHighlightId === message.id ? { outline: '2px solid #3b82f6', outlineOffset: '2px', borderRadius: '16px' } : {}),
                  }}>
                    <div className="chat-message-bubble" style={{
                      maxWidth: '75%',
                      minWidth: 0, // S55 fix : permet au overflow-x:auto des code blocks de s'appliquer
                      padding: '16px 20px',
                      borderRadius: '16px',
                      background: message.isUser
                        ? (message.authorIsSelf === false ? 'rgba(167,139,250,0.14)' : 'var(--accent-light)')
                        : 'var(--card-bg)',
                      color: 'var(--text)',
                      boxShadow: 'var(--shadow)',
                      fontSize: '14px',
                      lineHeight: '1.5',
                      position: 'relative',
                      overflow: 'hidden',
                      ...(message.isHelp ? { borderLeft: '3px solid #10b981' } : {}),
                      ...(message.isUser && message.authorIsSelf === false ? { borderLeft: '3px solid #a78bfa' } : {}),
 }}>
                      {/* Copier + Sauver sont dans le footer du message */}

                      {message.isUser
                        ? editingMsgId === message.id ? (
                            <div>
                              <textarea
                                value={editContent}
                                onChange={e => setEditContent(e.target.value)}
                                autoFocus
                                style={{
                                  width: '100%', minHeight: '60px', padding: '8px', borderRadius: '8px',
                                  border: '1px solid var(--card-border)', background: 'var(--input-bg)',
                                  color: 'var(--text)', fontSize: '13px', resize: 'vertical',
                                  fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
                                }}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' && e.ctrlKey) handleEditMessage(message.id);
                                  if (e.key === 'Escape') { setEditingMsgId(null); setEditContent(''); }
                                }}
                              />
                              <div style={{ display: 'flex', gap: '6px', marginTop: '6px', justifyContent: 'flex-end' }}>
                                <button onClick={() => { setEditingMsgId(null); setEditContent(''); }}
                                  style={{ padding: '4px 10px', borderRadius: '4px', border: '1px solid var(--card-border)', background: 'var(--card-bg)', color: 'var(--text-secondary)', fontSize: '11px', cursor: 'pointer' }}>
                                  Annuler
                                </button>
                                <button onClick={() => handleEditMessage(message.id)}
                                  style={{ padding: '4px 10px', borderRadius: '4px', border: 'none', background: '#1D4ED8', color: 'white', fontSize: '11px', cursor: 'pointer' }}>
                                  Sauver
                                </button>
                              </div>
                            </div>
                          ) : (
                          <div>
                            {message.pasteMetadata && <PastedContentChip pasteItems={message.pasteMetadata} />}
                            <div style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{message.content}</div>
                          </div>
                          )
                        : message.streaming
                          ? <>
                              <StreamStatus currentStep={currentStep} isStreaming={isStreaming} completedGroups={completedGroups} />
                              {streamingContent && <MarkdownMessage content={displayedStreamContent || streamingContent} images={streamingImages} dialecteMeta={streamingDialecteMeta} onPreview={projShowPreview} />}
                              {!streamingContent && !currentStep && <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>...</span>}
                              {streamingActions?.length > 0 && (
                                <ActionButtons items={streamingActions} rubriques={streamingRubriques} busyId={busyActionId} onAction={(it) => handleActionClick(it, message)} />
                              )}
                            </>
                          : <MarkdownMessage content={message.content} images={message.images || []} dialecteMeta={message.dialecte_meta || null} onPreview={projShowPreview} />
                      }

                      {!message.isUser && !message.streaming && !dismissedActions.has(message.id) && (message.actions?.length > 0) && (
                        <ActionButtons items={message.actions} rubriques={message.actionRubriques} busyId={busyActionId} onAction={(it) => handleActionClick(it, message)} />
                      )}

                   {!message.streaming && (
                   <div style={{ fontSize: '12px', opacity: 0.7, marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
  {message.isUser ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', justifyContent: 'space-between' }}>
      <span style={{ color: 'var(--text-secondary)' }}>
        {(message.authorName && message.authorIsSelf === false
          ? message.authorName
          : (user?.username || 'Utilisateur'))} le {new Date(message.timestamp).toLocaleDateString('fr-FR')} à {new Date(message.timestamp).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}
        {message.authorIsSelf === false && (
          <span style={{
            marginLeft: 6, padding: '1px 6px', borderRadius: 8,
            background: 'rgba(167,139,250,0.18)', color: '#a78bfa',
            fontSize: 10, fontWeight: 600, letterSpacing: 0.3,
          }}>COLLAB</span>
        )}
      </span>
      {message.authorIsSelf !== false && (
      <div className="msg-actions" style={{ display: 'flex', gap: '2px', opacity: 0, transition: 'opacity 0.15s' }}>
        <button onClick={() => { setEditingMsgId(message.id); setEditContent(message.content); }} title="Modifier (régénère la réponse si le contenu change)"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px' }}>
          <Pencil size={11} />
        </button>
        <button onClick={() => handleRerun(message)} title="Relancer cette requête (remplace les réponses suivantes)" disabled={isLoading || isStreaming}
          style={{ background: 'none', border: 'none', cursor: (isLoading || isStreaming) ? 'not-allowed' : 'pointer', color: '#a78bfa', padding: '2px', opacity: (isLoading || isStreaming) ? 0.4 : 1 }}>
          <RotateCcw size={11} />
        </button>
        {/* Session F — Coffre-fort sur message user */}
        {!vaultedMsgIds.has(message.id) && (
        <button
          onClick={() => {
            const raw = message.conversationId || message.id;
            const n = typeof raw === 'number' ? raw : parseInt(String(raw).replace('_user', '').replace('_ai', ''));
            const convId = Number.isFinite(n) && n > 0 ? n : null;
            setVaultModal({
              messageId: message.id,
              conversationId: convId,
              role: 'user',
              content: message.content,
            });
          }}
          title="Envoyer au coffre-fort (chiffrement + retrait mémoire IA)"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#a78bfa', padding: '2px' }}>
          <Lock size={11} />
        </button>
        )}
        {!isLastMessage && (
        <button onClick={() => handleTruncate(message)} title="Supprimer d'ici"
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}>
          <Scissors size={11} />
        </button>
        )}
      </div>
      )}
    </div>
  ) : (
    <>
    <span>
      <span style={{ color: message.isHelp ? '#10b981' : '#1D4ED8' }}>&#9679;</span> {message.isHelp ? 'Guide ITRIIA' : 'ITRIIA Route'} {message.model ? `\u2022 ${message.model}` : ''} le {new Date(message.timestamp).toLocaleDateString('fr-FR')} à {new Date(message.timestamp).toLocaleTimeString('fr-FR', {hour: '2-digit', minute: '2-digit'})}
      {message.isHelp && <span style={{ marginLeft: '6px', padding: '1px 6px', borderRadius: '4px', background: 'rgba(16,185,129,0.12)', color: '#10b981', fontSize: '10px', fontWeight: 600 }}>Guide</span>}
      {message.cost && <CostIndicator cost={message.cost} sessionCost={sessionCost} />}
    </span>
    {message.content && message.content.length > 200 && (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {/* Mini-prompt nom de fichier (inline) */}
        {savePromptMsgId === message.id && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '3px', animation: 'fadeIn 0.15s ease' }}>
            <input
              type="text"
              value={saveFileName}
              onChange={(e) => setSaveFileName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveMessageAsFile(message.content, message.id); if (e.key === 'Escape') setSavePromptMsgId(null); }}
              autoFocus
              style={{
                width: '160px', padding: '2px 6px', fontSize: '10px',
                border: '1px solid rgba(29,78,216,0.4)', borderRadius: '4px',
                background: 'var(--input-bg)', color: 'var(--text)',
                outline: 'none',
              }}
              placeholder="nom-du-fichier.md"
            />
            <button onClick={() => saveMessageAsFile(message.content, message.id)} title="Confirmer"
              style={{
                background: '#1D4ED8', border: 'none', borderRadius: '4px',
                padding: '2px 6px', cursor: 'pointer', fontSize: '10px', color: 'white',
                display: 'flex', alignItems: 'center', gap: '2px',
              }}>
              <Check size={10} /> OK
            </button>
            <button onClick={() => setSavePromptMsgId(null)} title="Annuler"
              style={{
                background: 'var(--input-bg)', border: '1px solid var(--card-border)', borderRadius: '4px',
                padding: '2px 5px', cursor: 'pointer', fontSize: '10px', color: 'var(--text-secondary)',
              }}>
              ✕
            </button>
          </div>
        )}
        {/* Bouton Sauver — grisé après sauvegarde */}
        {savePromptMsgId !== message.id && (
          <button
            onClick={() => !savedMsgIds.has(message.id) && openSavePrompt(message.content, message.id)}
            title={savedMsgIds.has(message.id) ? 'Déjà sauvegardé' : 'Sauvegarder comme fichier projet'}
            disabled={savingMsgId === message.id || savedMsgIds.has(message.id)}
            style={{
              background: savedMsgIds.has(message.id) ? 'rgba(34,197,94,0.15)' : savingMsgId === message.id ? '#1D4ED8' : 'rgba(29,78,216,0.12)',
              border: `1px solid ${savedMsgIds.has(message.id) ? 'rgba(34,197,94,0.3)' : 'rgba(29,78,216,0.3)'}`,
              borderRadius: '4px',
              padding: '2px 6px', cursor: savedMsgIds.has(message.id) ? 'default' : savingMsgId === message.id ? 'wait' : 'pointer',
              fontSize: '10px',
              color: savedMsgIds.has(message.id) ? '#22c55e' : savingMsgId === message.id ? 'white' : '#3b82f6',
              display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.2s',
              opacity: savedMsgIds.has(message.id) ? 0.6 : savingMsgId === message.id ? 0.7 : 1,
            }}>
            {/* S57 — icône-only (cf. Soufari : alléger la rangée) */}
            {savedMsgIds.has(message.id) ? <Check size={13} /> : <Save size={13} />}
          </button>
        )}
        {/* Session F — Bouton Coffre-fort (envoyer la réponse au coffre chiffré) */}
        {!vaultedMsgIds.has(message.id) && (
        <button
          onClick={() => {
            // Le bloc message peut être identifié soit par message.id (form '12345_ai' / '12345_user' / int)
            // soit par message.conversationId (int direct côté DB project_conversations.id).
            // Pattern miroir L500/L606 : strip suffix puis parseInt.
            const raw = message.conversationId || message.id;
            const n = typeof raw === 'number' ? raw : parseInt(String(raw).replace('_user', '').replace('_ai', ''));
            const convId = Number.isFinite(n) && n > 0 ? n : null;
            setVaultModal({
              messageId: message.id,
              conversationId: convId,
              role: 'assistant',
              content: message.content,
            });
          }}
          title="Envoyer au coffre-fort (chiffrement + retrait mémoire IA)"
          style={{
            background: 'rgba(167,139,250,0.12)',
            border: '1px solid rgba(167,139,250,0.3)', borderRadius: '4px',
            padding: '2px 6px', cursor: 'pointer', fontSize: '10px',
            color: '#a78bfa',
            display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.2s'
          }}>
          <Lock size={10} />
          Coffre
        </button>
        )}
        {vaultedMsgIds.has(message.id) && (
        <span style={{
          background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.2)',
          borderRadius: '4px', padding: '2px 6px', fontSize: '10px', color: '#a78bfa',
          display: 'inline-flex', alignItems: 'center', gap: '3px',
        }}>
          <Lock size={10} /> Coffre
        </span>
        )}
        {/* Bouton Aperçu — ouvre le message en split view */}
        <button onClick={() => { projShowPreview({ type: 'markdown', content: message.content, filename: `Réponse ITRIIA` }); onSplitViewChange?.(true); }} title="Aperçu en panneau latéral"
          style={{
            background: 'rgba(245,158,11,0.12)',
            border: '1px solid rgba(245,158,11,0.3)', borderRadius: '4px',
            padding: '2px 6px', cursor: 'pointer', fontSize: '10px',
            color: '#f59e0b',
            display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.2s'
          }}>
          {/* S57 — icône-only */}
          <Eye size={13} />
        </button>
        {/* Bouton Éditer — ouvre le split view en mode édition (nettoyage avant sauvegarde fichier/téléchargement) */}
        <button onClick={() => { projShowPreview({ type: 'markdown', content: message.content, filename: `Réponse ITRIIA`, editable: true }); onSplitViewChange?.(true); }} title="Éditer pour nettoyer et produire un livrable"
          style={{
            background: 'rgba(167,139,250,0.12)',
            border: '1px solid rgba(167,139,250,0.3)', borderRadius: '4px',
            padding: '2px 6px', cursor: 'pointer', fontSize: '10px',
            color: '#a78bfa',
            display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.2s'
          }}>
          {/* S57 — icône-only */}
          <Pencil size={13} />
        </button>
        {/* Session J+1 — Bouton Newsletter (envoyer le message comme campagne mailing) */}
        <button
          onClick={() => setMailingModal({
            subject: extractSubjectFromMarkdown(message.content),
            bodyMd: message.content,
          })}
          title="Diffuser cette réponse comme newsletter"
          style={{
            background: 'rgba(236,72,153,0.12)',
            border: '1px solid rgba(236,72,153,0.3)', borderRadius: '4px',
            padding: '2px 6px', cursor: 'pointer', fontSize: '10px',
            color: '#ec4899',
            display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.2s'
          }}>
          <Mail size={10} />
          Newsletter
        </button>
        {/* S57 B.5 — Bouton "Vers chapitre" — append de la réponse IA au chapitre courant */}
        {hasBook && (
          <button
            onClick={async () => {
              const bid = projectData?.book_id;
              const cid = window.__itriiaCurrentChapterId;
              if (!bid) { toast.error('Aucun livre lié à ce projet'); return; }
              if (!cid) { toast.warning('Ouvre d\'abord un chapitre dans l\'onglet Livre'); return; }
              setAppendingMsgId(message.id);
              try {
                await appendToChapter(bid, cid, message.content);
                setAppendedMsgIds(prev => new Set(prev).add(message.id));
                toast.success('Ajouté au chapitre');
              } catch (e) {
                toast.error('Erreur : ' + (e.detail?.message || e.message || 'inconnue'));
              } finally {
                setAppendingMsgId(null);
              }
            }}
            disabled={appendingMsgId === message.id || appendedMsgIds.has(message.id)}
            title="Ajouter au chapitre courant de l'atelier"
            style={{
              background: appendedMsgIds.has(message.id) ? '#8b5cf6' : 'rgba(139,92,246,0.12)',
              border: '1px solid rgba(139,92,246,0.3)', borderRadius: '4px',
              padding: '2px 6px',
              cursor: (appendingMsgId === message.id || appendedMsgIds.has(message.id)) ? 'default' : 'pointer',
              fontSize: '10px',
              color: appendedMsgIds.has(message.id) ? 'white' : '#a78bfa',
              display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.2s',
              opacity: appendingMsgId === message.id ? 0.7 : 1,
            }}>
            {/* S57 — icône-only (label dans le tooltip) */}
            {appendedMsgIds.has(message.id) ? <Check size={13} /> : <BookOpen size={13} />}
          </button>
        )}
        {/* Bouton Copier (markdown source) */}
        <button onClick={() => copyResponse(message.content, message.id + '_bottom')} title="Copier en markdown source (gras, titres, code…)"
          style={{
            background: copiedMsgId === (message.id + '_bottom') ? '#22c55e' : 'var(--input-bg)',
            border: '1px solid var(--card-border)', borderRadius: '4px',
            padding: '2px 6px', cursor: 'pointer', fontSize: '10px',
            color: copiedMsgId === (message.id + '_bottom') ? 'white' : 'var(--text-secondary)',
            display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.2s'
          }}>
          {copiedMsgId === (message.id + '_bottom') ? <Check size={10} /> : <Copy size={10} />}
          {copiedMsgId === (message.id + '_bottom') ? 'Copié' : 'Markdown'}
        </button>
        {/* QW3 (S56) — Bouton Copier sans formatage (texte brut, idéal pour coller dans email / Word / Google Docs) */}
        <button onClick={() => copyResponsePlain(message.content, message.id + '_bottom_plain')} title="Copier sans formatage (idéal email, Word, Google Docs)"
          style={{
            background: copiedMsgId === (message.id + '_bottom_plain') ? '#22c55e' : 'var(--input-bg)',
            border: '1px solid var(--card-border)', borderRadius: '4px',
            padding: '2px 6px', cursor: 'pointer', fontSize: '10px',
            color: copiedMsgId === (message.id + '_bottom_plain') ? 'white' : 'var(--text-secondary)',
            display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.2s'
          }}>
          {copiedMsgId === (message.id + '_bottom_plain') ? <Check size={10} /> : <Copy size={10} />}
          {copiedMsgId === (message.id + '_bottom_plain') ? 'Copié' : 'Texte brut'}
        </button>
        {/* Bouton Truncate — masqué sur le dernier message */}
        {!isLastMessage && (
        <button onClick={() => handleTruncate(message)} title="Supprimer d'ici"
          style={{
            background: 'rgba(239,68,68,0.08)',
            border: '1px solid rgba(239,68,68,0.2)', borderRadius: '4px',
            padding: '2px 6px', cursor: 'pointer', fontSize: '10px',
            color: '#ef4444',
            display: 'flex', alignItems: 'center', gap: '3px', transition: 'all 0.2s'
          }}>
          <Scissors size={10} />
        </button>
        )}
      </div>
    )}
    </>
  )}
</div>
                   )}
                    </div>
                  </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />

              {/* S57 — bouton scroll-to-bottom factorisé dans <ScrollToBottomButton>,
                  réutilisé aussi par ConversationChat (chat général) et ChapterChatPane
                  (Atelier). Hystérésis 200/80 + couleur neutre adaptative en interne. */}
              <ScrollToBottomButton containerRef={chatContainerRef} />
            </div>

            {/* Toolbar d'outils au-dessus de la zone de saisie */}
           <InputToolbar
  onAnalyze={handleAnalyze}
  onSummary={handleSummary}
  onInsertTemplate={handleInsertTemplate}
  onClear={handleClear}
  onBaseExpertise={handleBaseExpertise}
  onManageTemplates={() => { setEditTemplateData(null); setShowTemplateManager(true); }}
  onEditTemplate={(t) => { setEditTemplateData(t); setShowTemplateManager(true); }}
  helpMode={helpMode}
  onToggleHelp={() => setHelpMode(h => !h)}
  onConsolidate={activeSessionId ? () => setShowConsolidateModal(true) : null}
  currentProject={{ id: projectId, name: projectData?.name }}
  messageCount={sessionMessageCount || chatMessages.length}
  forcedModel={forcedModel}
  onForcedModelChange={setForcedModel}
  isStreaming={isStreaming}
  templatesVersion={templatesVersion}
/>

            {/* Bannière mode aide */}
            {helpMode && (
              <div style={{
                padding: '6px 14px', marginBottom: '6px', borderRadius: '10px',
                display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '500',
                background: 'rgba(16,185,129,0.08)',
                border: '1px solid rgba(16,185,129,0.25)',
                color: '#10b981',
              }}>
                <span style={{ fontSize: '14px' }}>📚</span>
                Mode Aide actif — Réponses basées sur le Guide ITRIIA. Aucune donnée projet utilisée.
                <button onClick={() => setHelpMode(false)} style={{
                  marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer',
                  color: '#10b981', fontSize: '16px', lineHeight: 1, padding: '0 2px',
                }}>×</button>
              </div>
            )}

            {/* Alerte budget SSE */}
            {budgetAlert && (
              <div style={{
                padding: '8px 14px', marginBottom: '8px', borderRadius: '10px',
                display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', fontWeight: '500',
                background: budgetAlert.type === 'exceeded' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
                border: `1px solid ${budgetAlert.type === 'exceeded' ? 'rgba(239,68,68,0.25)' : 'rgba(245,158,11,0.25)'}`,
                color: budgetAlert.type === 'exceeded' ? '#ef4444' : '#f59e0b',
              }}>
                <span style={{ fontSize: '14px' }}>{budgetAlert.type === 'exceeded' ? '🚫' : '⚠️'}</span>
                {budgetAlert.type === 'exceeded'
                  ? `Budget dépassé (${budgetAlert.budget?.percent}%) — $${budgetAlert.budget?.spent_usd?.toFixed(4)} / $${budgetAlert.budget?.budget_usd?.toFixed(2)}`
                  : `Attention : ${budgetAlert.budget?.percent}% du budget consommé — $${budgetAlert.budget?.spent_usd?.toFixed(4)} / $${budgetAlert.budget?.budget_usd?.toFixed(2)}`
                }
              </div>
            )}

            {/* Zone de saisie premium */}
            <div style={{
              padding: '12px 16px',
              background: 'var(--input-bg)',
              borderRadius: '16px',
              border: '1px solid var(--input-border)',
              boxShadow: 'var(--shadow)',
              transition: 'border-color 0.2s, box-shadow 0.2s'
            }}
              onFocus={e => { e.currentTarget.style.borderColor = '#93c5fd'; e.currentTarget.style.boxShadow = '0 2px 16px rgba(59,130,246,0.12)'; }}
              onBlur={e => { e.currentTarget.style.borderColor = 'var(--input-border)'; e.currentTarget.style.boxShadow = 'var(--shadow)'; }}
            >
              {/* Fichiers colles / attaches */}
              <PasteAttachments
                attachments={attachments}
                onRemove={removeAttachment}
                onDestinationChange={updateDestination}
                showDestinationToggle={true}
              />
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <PlusMenu
                  onFilesSelected={addFiles}
                  onPasteContent={() => setShowPasteModal(true)}
                  onScreenCapture={() => toast.info('Utilisez Ctrl+V pour coller une capture d\'écran')}
                  onLinkFile={() => setActiveTab('files')}
                  onCreateNote={() => setShowNoteModal(true)}
                  onTriggerN8N={() => toast.info('Ouvrez les outils N8N depuis la toolbar')}
                  onInsertTemplate={() => toast.info('Ouvrez les templates depuis la toolbar')}
                  onAnalyzeUrl={addUrl}
                  onOrganizeFiles={() => setShowOrganizerModal(true)}
                  isProjectContext={true}
                  projectId={projectId}
                  disabled={isLoading}
                />
                <textarea
                  ref={textareaRef}
                  value={chatInput}
                  onChange={(e) => {
                    setChatInput(e.target.value);
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  onPaste={handlePaste}
                  placeholder="Écrivez votre message..."
                  style={{
                    flex: 1,
                    minHeight: '44px',
                    maxHeight: '160px',
                    padding: '8px 4px',
                    border: 'none',
                    fontSize: '15px',
                    lineHeight: '1.5',
                    resize: 'none',
                    fontFamily: 'inherit',
                    outline: 'none',
                    color: 'var(--text)',
                    background: 'transparent'
                  }}
                  disabled={isLoading}
                  rows={1}
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--card-border)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {/* Badge MP */}
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '500',
                    background: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0'
                  }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e' }}></span>
                    MP
                  </span>
                  {/* Badge mode */}
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '500',
                    background: cmActive ? '#fef3c7' : '#f3f4f6',
                    color: cmActive ? '#92400e' : '#6b7280',
                    border: cmActive ? '1px solid #fde68a' : '1px solid #e5e7eb'
                  }}>
                    {cmActive ? '🔮 Expertise' : '📝 Standard'}
                  </span>
                </div>
                {/* Boutons Voice + Send groupés */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}>
                  <VoiceButton
                    isOpen={showVoiceWidget}
                    onClick={() => setShowVoiceWidget(!showVoiceWidget)}
                  />
                  <button
                    onClick={isStreaming ? () => { abortStream(); setIsLoading(false); } : sendMessage}
                    disabled={!isStreaming && (isLoading || (!chatInput.trim() && attachments.length === 0))}
                    style={{
                      width: '36px', height: '36px',
                      borderRadius: '50%',
                      border: 'none',
                      cursor: isStreaming ? 'pointer'
                        : (isLoading || (!chatInput.trim() && attachments.length === 0)) ? 'not-allowed' : 'pointer',
                      background: isStreaming
                        ? 'transparent'
                        : (isLoading || (!chatInput.trim() && attachments.length === 0))
                          ? 'var(--card-border)'
                          : 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                      color: 'white',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.2s',
                      boxShadow: isStreaming
                        ? '0 0 16px rgba(220,38,38,0.25)'
                        : (isLoading || (!chatInput.trim() && attachments.length === 0)) ? 'none' : '0 2px 8px rgba(37,99,235,0.3)',
                      position: 'relative',
                    }}
                    onMouseEnter={e => {
                      if (isStreaming || (!isLoading && (chatInput.trim() || attachments.length > 0)))
                        e.currentTarget.style.transform = 'scale(1.08)';
                    }}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    title={isStreaming ? 'Arrêter la génération' : 'Envoyer'}
                  >
                    {isStreaming ? (
                      <>
                        {/* Cercle pointillés qui tournent autour — IA en train de travailler */}
                        <div style={{
                          position: 'absolute',
                          inset: 0,
                          borderRadius: '50%',
                          border: '1.5px dashed #dc2626',
                          animation: 'spin 3s linear infinite',
                          pointerEvents: 'none',
                        }} />
                        {/* Carré plein central — clic pour stopper */}
                        <div style={{
                          width: '11px', height: '11px',
                          background: '#dc2626',
                          borderRadius: '1.5px',
                          boxShadow: '0 0 6px rgba(220,38,38,0.5)',
                        }} />
                      </>
                    ) : isLoading ? (
                      <div style={{
                        width: '14px', height: '14px',
                        border: '2px solid white',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }} />
                    ) : (
                      <Send size={15} />
                    )}
                  </button>
                  {showVoiceWidget && (
                    <VoiceWidget
                      onClose={() => setShowVoiceWidget(false)}
                      onInjectToChat={(text) => {
                        setChatInput(prev => prev ? prev + '\n\n' + text : text);
                        setShowVoiceWidget(false);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
          </SplitChatView>
        )}

        {/* Modal Coller du contenu */}
        {showPasteModal && (
          <>
            <div
              onClick={() => setShowPasteModal(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 900,
                background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
                animation: 'drawerFadeIn 0.15s ease',
              }}
            />
            <div style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              zIndex: 901, width: '520px', maxWidth: '90vw', maxHeight: '80vh', overflowY: 'auto',
              background: 'var(--modal-bg, var(--card-bg))',
              border: '1px solid var(--card-border)', borderRadius: '14px',
              boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
              animation: 'fadeInScale 0.2s ease-out',
            }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--card-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Coller du contenu</span>
                <button onClick={() => setShowPasteModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px' }}>✕</button>
              </div>
              <div style={{ padding: '16px' }}>
                <PasteAsFileWidget
                  projectId={projectId}
                  onFileSaved={() => { setShowPasteModal(false); toast.success('Fichier sauvegardé'); if (onFilesChanged) onFilesChanged(); }}
                />
              </div>
            </div>
            <style>{`@keyframes fadeInScale { from { opacity:0; transform: translate(-50%,-50%) scale(0.95); } to { opacity:1; transform: translate(-50%,-50%) scale(1); } }`}</style>
          </>
        )}

        {/* Modal Créer une note */}
        {showNoteModal && (
          <>
            <div
              onClick={() => setShowNoteModal(false)}
              style={{
                position: 'fixed', inset: 0, zIndex: 900,
                background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(2px)',
                animation: 'drawerFadeIn 0.15s ease',
              }}
            />
            <div style={{
              position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              zIndex: 901, width: '480px', maxWidth: '90vw',
              background: 'var(--modal-bg, var(--card-bg))',
              border: '1px solid var(--card-border)', borderRadius: '14px',
              boxShadow: '0 16px 48px rgba(0,0,0,0.3)',
              animation: 'fadeInScale 0.2s ease-out',
            }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--card-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text)' }}>Créer une note</span>
                <button onClick={() => setShowNoteModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '2px' }}>✕</button>
              </div>
              <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <input
                  autoFocus
                  value={noteTitle}
                  onChange={e => setNoteTitle(e.target.value)}
                  placeholder="Titre de la note (optionnel)"
                  style={{
                    padding: '8px 12px', borderRadius: '8px',
                    border: '1px solid var(--input-border)', background: 'var(--input-bg)',
                    color: 'var(--text)', fontSize: '13px', fontFamily: 'inherit', outline: 'none',
                  }}
                  onFocus={e => e.currentTarget.style.borderColor = '#93c5fd'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--input-border)'}
                />
                <textarea
                  value={noteContent}
                  onChange={e => setNoteContent(e.target.value)}
                  placeholder="Contenu de la note..."
                  rows={8}
                  style={{
                    padding: '10px 12px', borderRadius: '8px',
                    border: '1px solid var(--input-border)', background: 'var(--input-bg)',
                    color: 'var(--text)', fontSize: '13px', fontFamily: 'monospace',
                    resize: 'vertical', minHeight: '120px', outline: 'none', lineHeight: '1.5',
                  }}
                  onFocus={e => e.currentTarget.style.borderColor = '#93c5fd'}
                  onBlur={e => e.currentTarget.style.borderColor = 'var(--input-border)'}
                  onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleSaveNote(); }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Ctrl+Enter pour sauvegarder</span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setShowNoteModal(false)}
                      style={{
                        padding: '6px 16px', borderRadius: '8px', fontSize: '12px',
                        border: '1px solid var(--card-border)', background: 'transparent',
                        color: 'var(--text-secondary)', cursor: 'pointer',
                      }}
                    >Annuler</button>
                    <button
                      onClick={handleSaveNote}
                      disabled={!noteContent.trim()}
                      style={{
                        padding: '6px 16px', borderRadius: '8px', fontSize: '12px', fontWeight: '600',
                        border: 'none', cursor: noteContent.trim() ? 'pointer' : 'not-allowed',
                        background: noteContent.trim() ? 'linear-gradient(135deg, #2563eb, #1d4ed8)' : 'var(--card-border)',
                        color: 'white', transition: 'all 0.2s',
                        boxShadow: noteContent.trim() ? '0 2px 8px rgba(37,99,235,0.3)' : 'none',
                      }}
                    >Sauvegarder</button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'overview' && (
          <ProjectSettings
            projectId={projectId}
            projectData={projectData}
            onProjectUpdate={(updated) => {
              setProjectData(prev => ({ ...prev, ...updated }));
              if (onProjectUpdated) onProjectUpdated({ id: projectId, ...updated });
            }}
            onTabSwitch={setActiveTab}
            user={user}
          />
        )}

      {activeTab === 'files' && (
  <FilesInterface user={user} projectId={projectId} onFilesChanged={onFilesChanged} onFileAnalyze={handleFileAnalyze} />
)}

        {activeTab === 'expertise' && (
          <ExpertiseBasesPanel user={user} />
        )}

        {activeTab === 'team' && (
          <ProjectTeam projectId={projectId} user={user} />
        )}

        {activeTab === 'trans-memory' && (
          <TransMemoryTab projectId={projectId} user={user} />
        )}

        {activeTab === 'analytics' && (
          <ProjectAnalytics projectId={projectId} user={user} />
        )}

        {activeTab === 'book' && hasBook && (
          <ChapterWorkspace
            bookId={projectData.book_id}
            projectId={projectId}
            onCurrentChapterChanged={(cid) => { window.__itriiaCurrentChapterId = cid; }}
          />
        )}

        {activeTab === 'family' && hasFamily && (
          <div style={{ height: 'calc(100vh - 260px)', minHeight: 520 }}>
            <FamilyGraph projectId={projectId} />
          </div>
        )}

        {activeTab === 'media' && hasMediaConnector && (
          <MediaCockpit projectId={projectId} onTabSwitch={setActiveTab} />
        )}

        {activeTab === 'pilot-mutation' && hasWpSource && (
          <div style={{ height: 'calc(100vh - 260px)', minHeight: 520 }}>
            <PilotMutationPanel projectId={projectId} canWrite={!isGuest} />
          </div>
        )}
      </div>

      {/* Modal gestion templates */}
      <TemplateManager
        isOpen={showTemplateManager}
        onClose={() => { setShowTemplateManager(false); setEditTemplateData(null); }}
        onTemplatesChanged={() => setTemplatesVersion(v => v + 1)}
        initialEdit={editTemplateData}
      />

      {/* S48 — Modal consolidation mémoire */}
      {showConsolidateModal && activeSessionId && (
        <ConsolidateModal
          projectId={projectId}
          sessionRef={activeSessionId}
          sessionTitle={sessions.find(s => s.id === activeSessionId)?.title}
          onClose={() => setShowConsolidateModal(false)}
          onDone={({ consolidationId }) => {
            // Recharge les sessions et la liste fichiers
            if (typeof loadAllSessions === 'function') loadAllSessions();
            if (typeof onFilesChanged === 'function') onFilesChanged();
          }}
        />
      )}

      {/* S59 — Modal organisation IA des fichiers */}
      {showOrganizerModal && (
        <FileOrganizerModal
          projectId={projectId}
          projectName={projectData?.name}
          onClose={() => setShowOrganizerModal(false)}
          onApplied={() => {
            // Recharge liste fichiers + invalide cache (contenu change)
            if (typeof onFilesChanged === 'function') onFilesChanged();
            setOrganizerCache({});
          }}
          getCachedResult={getOrganizerCache}
          putCachedResult={putOrganizerCache}
        />
      )}

      {/* Session F — Modale envoi au coffre-fort */}
      <SendToVaultModal
        open={!!vaultModal}
        onClose={() => setVaultModal(null)}
        projectId={projectId}
        conversationId={vaultModal?.conversationId}
        role={vaultModal?.role}
        content={vaultModal?.content || ''}
        onConfirmed={() => {
          // Marque le bloc comme vauté localement (UI immédiate, on ne recharge pas tout)
          if (vaultModal?.messageId) {
            setVaultedMsgIds(prev => new Set(prev).add(vaultModal.messageId));
          }
          // Recharge la liste de fichiers projet (vault apparaîtra dans Coffre-fort)
          if (typeof onFilesChanged === 'function') onFilesChanged();
        }}
      />

      {/* Session J+1 — Composer mailing pré-rempli depuis un message IA */}
      {mailingModal && (
        <MailingComposerModal
          initial={{ subject: mailingModal.subject, bodyMd: mailingModal.bodyMd }}
          onClose={() => setMailingModal(null)}
          onSent={() => {
            setMailingModal(null);
            toast.success('Campagne créée — voir Mailing pour le suivi.');
          }}
        />
      )}
    </div>
  );
};

export default ProjectDetail;
